import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { FlashList } from '@shopify/flash-list'
import PostFlags from './PostFlags'
// import { fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { Image } from 'expo-image'
import { View, Text, RefreshControl, useWindowDimensions, Platform, ScrollView, Animated, FlatList, AppState } from 'react-native'
import NetInfo from '@react-native-community/netinfo'
import theme from '../../../theme'
import { apiUrl } from '../../config/config'
import { update_complete_local_posts } from '../../db/posts/index'
import { useBottomSheet } from '../customUtils/context/BottomSheetContext'
import { useIsFocused, useRoute } from '@react-navigation/native'
import styles from './PostsListStyles'
import TermsAndConditions from '../modals/TermsAndConditions'
import { StorageAdapter } from '../../utils/AsyncStorage'
import { ASYNC_STORAGE_KEYS } from '../stores/constants/constants.storage'
import { PostsServices } from '../../../database/posts.database'
import { WEBSOCKET_TYPES } from '../../utils/Constants'
import { getAuthToken } from '../../utils/functions.utils'
import { TrackingServices } from '../../../database/tracking.database'
import { useWebSocket } from '../appBar/context/WebSocketContext'
import { getLanguage } from '../appBar/context/LocalizationContext'
import mediaSingleton from '../../utils/mediaSingleton'
import { useVerifyCognito } from './hooks/useVerifyCognito'
import { UserServices } from '../../../database/user.database'
import { request_get_reactions_wall_according_id, request_get_terms_and_conditions, request_get_wall } from '../../services/posts.services'
import { usePostContext } from './context/posts.context'
import { useLabelLoadingStore } from '../../redux/zustand/posts-label-loading-store'
import { getLocation } from '../appBar/context/LocationContext'
import { getConection } from '../appBar/context/HeaderContext'
import { getAlert } from '../appBar/context/AlertsContext'
import { PostsItemDefault } from './ItemDefault'
import { FormView } from '../stores/ExecutionView'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { resetCache } from '../../utils/FetchServices'
import { useInteractionsStore } from '../../redux/zustand/interactionsPostStore'
import { useTermsAndConditionsStore } from '../../redux/zustand/termsAndContionsStore'
import { eventEmitter, downloadContent, pruneDownloadedContent, downloadAvatarUser, pruneAvatars } from '../../utils/functions.utils'
import { isWeb } from '../../utils/platformUtils'
import type { LabelItem, PostItem, OnViewableItemsChanged } from '../../types/posts'
import type { LabelIdT } from './context/posts.context'
import { debug, info, warn, error, lazy, createThrottled } from '../../utils/logger'
import SizeCache from '../../utils/sizeCache'
import { useBreakpoint } from '@/redux/zustand/useDimensionStore'
import { useRightSidebar } from '../web/context/RightSidebarContext'

// Variable global para controlar la emisión del evento firstBatchReady
let globalFirstBatchReadyEmitted = false

// Función para resetear el estado cuando sea necesario
export const resetFirstBatchReadyState = () => {
    globalFirstBatchReadyEmitted = false
}

// Fusiona arrays de reacciones (local y remoto) sumando count por una clave estable
const mergeReactionsCounts = (localArr: any[], remoteArr: any[]) => {
    const norm = (arr: any[]) => (Array.isArray(arr) ? arr : [])
    const A = norm(localArr)
    const B = norm(remoteArr)
    const map = new Map<string, any>()
    const upsert = (r: any) => {
        if (!r) return
        // Clave estable: reaction_id | _id | name | index
        const key = String(r?.reaction_id ?? r?._id ?? r?.name ?? r?.index ?? '')
        if (!key) return
        const prev = map.get(key) || { ...r, count: 0 }
        const next = {
            ...prev,
            name: r?.name ?? prev?.name,
            index: r?.index ?? prev?.index,
            reaction_id: r?.reaction_id ?? prev?.reaction_id,
            _id: r?._id ?? prev?._id,
            isSelected: prev?.isSelected || r?.isSelected || false,
            count: (Number(prev?.count) || 0) + (Number(r?.count) || 0),
        }
        map.set(key, next)
    }
    A.forEach(upsert)
    B.forEach(upsert)
    // Ordenar por index si existe, sino por nombre
    return Array.from(map.values()).sort((x, y) => {
        const ix = (x?.index ?? 9999) as number
        const iy = (y?.index ?? 9999) as number
        if (ix !== iy) return ix - iy
        const nx = String(x?.name ?? '')
        const ny = String(y?.name ?? '')
        return nx.localeCompare(ny)
    })
}

interface PostsListProps {
    enableMasonryExperimental?: boolean
    // Alternativa web-only: Masonry vía CSS columns (sin mediciones ni JS layout)
    useCssColumnsMasonry?: boolean
}

let globalFetchAndSavePostsInProgress = false

const PostsList: React.FC<PostsListProps> = ({ enableMasonryExperimental = true, useCssColumnsMasonry = false }) => {
    // --- Web Diagnostics (pantalla en blanco) ---
    if (Platform.OS === 'web') {
        try {
            // Emitir sólo una vez por sesión ciertas métricas básicas
            ;(globalThis as any).__postsListDiagOnce = (globalThis as any).__postsListDiagOnce || 0
            if ((globalThis as any).__postsListDiagOnce < 3) {
                debug('PostsList', 'mount attempt', { ts: Date.now() })
                ;(globalThis as any).__postsListDiagOnce++
            }
        } catch {}
    }
    const { width: rawWidth, height } = useWindowDimensions()

    // Debounce del width para evitar loops infinitos al redimensionar
    const [width, setWidth] = useState(rawWidth)
    useEffect(() => {
        const timer = setTimeout(() => {
            setWidth(rawWidth)
        }, 150) // 150ms de debounce
        return () => clearTimeout(timer)
    }, [rawWidth])

    const baseColWidth = 440
    const PAGE_SIZE = 20
    // Cálculo de columnas
    const bp = useBreakpoint()
    const isMobile = bp === 'sm'
    const isTablet = Platform.OS !== 'web' && Math.min(width, height) >= 768
    // Web: cálculo de columnas con regla de "hueco >= 550px => sumar una columna"
    const dynamicColumns = useMemo(() => {
        if (Platform.OS !== 'web') return isTablet ? 3 : 1
        const tentative = Math.max(1, Math.floor(width / baseColWidth))
        const leftover = width - tentative * baseColWidth
        const cols = leftover >= baseColWidth ? tentative + 1 : tentative
        return Math.max(1, cols)
    }, [width, isTablet])

    // Tamaño estimado de ítem para estabilizar layout inicial de FlashList y reducir re-mediciones visibles
    const estimatedItemSize = useMemo(() => {
        try {
            // Estimar: altura media de media 16:9 + contenido textual/buttons (~200px)
            const mediaH = Math.round((Math.max(320, width - 16) * 9) / 16)
            return Math.max(360, mediaH + 200)
        } catch {
            return 560
        }
    }, [width])

    // (Bloque masonry reinsertado después de obtener dataPosts)
    // En web hemos desmontado navegación nativa; si no hay contexto de ruta, evitamos crash.
    let route: any = null
    try {
        route = useRoute()
    } catch (e) {
        if (Platform.OS === 'web') {
            route = { name: 'PostsList', params: {} }
        } else {
            throw e
        }
    }
    useVerifyCognito()

    const {
        setPostsContext,
        getPostsByLabel,
        postsContext,
        dataPosts,
        setDataPosts,
        updateViewPostsContext,
        getPostsFavourite,
        form,
        setForm,
        alreadySentForm,
        setAlreadySentForm,
    } = usePostContext()
    const { openBottomSheet, closeBottomSheet } = useBottomSheet()
    const { openRightSidebar, updateSidebarData } = useRightSidebar()
    const { post_id, setPostID } = (require('../appBar/context/GoToContext') as any).getGoTo() as {
        post_id?: string | number | null
        setPostID: (id: string | number | null) => void
    }
    const [posts, setPosts] = useState<PostItem[]>([])
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [selectedLabel, setSelectedLabel] = useState<LabelIdT | null>(-2 as LabelIdT)
    const [page, setPage] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const mainViewRef = useRef<any>(null)
    const [allReactions, setallReactions] = useState<any[]>([])
    const [refreshing, setRefreshing] = useState(false)
    const size = 50
    const [postVieweds, setPostVieweds] = useState(0)
    const lastScrollYRef = useRef(0)
    const [newlyAddedIds, setNewlyAddedIds] = useState<Set<string | number>>(new Set())
    const webSocketInstance = useWebSocket() as any
    const [viewedItemIndex, setViewedItemIndex] = useState(0)
    // Ref espejo + pipeline de debounce/rAF para reducir cambios bruscos al detener scroll
    const viewedItemIndexRef = useRef(0)
    const noCandidateTimerRef = useRef<any>(null)
    const lastHasCandidateRef = useRef<boolean>(false)
    const pendingViewedIndexRef = useRef<number | null>(null)
    const viewedIndexRafRef = useRef<number | null>(null)
    const lastCommittedViewedTsRef = useRef(0)
    // Info de estabilización de candidato a foco
    const focusCandidateRef = useRef<{ index: number; count: number; firstTs: number } | null>(null)
    const viewedSentSetRef = useRef<Set<string | number>>(new Set())
    const postsRef = useRef<PostItem[]>(postsContext as PostItem[])
    const { languageFile } = getLanguage()
    const [labels, setLabels] = useState<LabelItem[]>([
        { _id: -1, name: '', isFavourite: true },
        { _id: -2, name: languageFile.all, isSelected: true },
    ])
    const [lastSendView, setlastSendView] = useState<any[]>([])
    const isSendingViewPosts = useRef(false)
    const isFetchingMoreRef = useRef(false)
    const lastViewChangeTsRef = useRef(0)
    const currentTopVisibleIdRef = useRef<string | number | null>(null)
    const viewTimersRef = useRef<Map<string | number, any>>(new Map())
    // Timer para detectar cuando el scroll se detiene
    const scrollTimerRef = useRef<any>(null)
    // Bootstrap inicial para asegurar que el muro se cargue desde DB local en mobile, incluso si TTCC aún no está aceptado
    const initialBootDoneRef = useRef(false)
    // Control de reintentos de auth (para evitar loops de "no authToken available")
    const authRetryInfoRef = useRef<{ count: number; lastTs: number; backoffMs: number }>({ count: 0, lastTs: 0, backoffMs: 1500 })
    // TTCC throttling to avoid spamming service
    const getTermsInFlightRef = useRef(false)
    const lastGetTermsTsRef = useRef(0)

    // Control para evitar múltiples cargas progresivas simultáneas
    const isLoadingProgressivelyRef = useRef(false)
    const lastProgressiveLoadPostIdRef = useRef<string | number | null>(null)

    // Flicker gating: emitir cuando el primer media visible del primer post esté listo
    const firstScreenReadyEmittedRef = useRef(false)
    const handleFirstMediaReady = useCallback((postId: any, listIndex: any) => {
        try {
            if (!firstScreenReadyEmittedRef.current && (listIndex === 0 || listIndex == null)) {
                info('PostsList', 'Emitting app:firstScreenReady (top item media ready)', { postId, listIndex })
                eventEmitter.emit('app:firstScreenReady')
                firstScreenReadyEmittedRef.current = true
            }
        } catch {}
    }, [])

    // Fallback en Web: si ya hay posts visibles pero no llegó la señal del primer media,
    // emitimos app:firstScreenReady tras un breve delay para no bloquear la pantalla.
    useEffect(() => {
        if (Platform.OS !== 'web') return
        try {
            const hasData = Array.isArray(dataPosts) && dataPosts.length > 0
            if (hasData && !firstScreenReadyEmittedRef.current) {
                const t = setTimeout(() => {
                    if (!firstScreenReadyEmittedRef.current) {
                        info('PostsList', 'Web fallback -> Emitting app:firstScreenReady (posts loaded)')
                        eventEmitter.emit('app:firstScreenReady')
                        firstScreenReadyEmittedRef.current = true
                    }
                }, 1600)
                return () => clearTimeout(t)
            }
        } catch {}
    }, [dataPosts])

    const changeLoading = useLabelLoadingStore((state) => state.changeLoading)
    const loadingFetchInternal = useLabelLoadingStore((state) => state.loading)
    const changeLabel = useLabelLoadingStore((state) => state.changeLabel)
    const labelStore = useLabelLoadingStore((state) => state.label)
    const setInteractions = useInteractionsStore((state) => state.setInteractions)

    // Normalización inicial: asegurar que todas las claves y post_id sean strings
    useEffect(() => {
        // Cargar el muro (al menos desde DB local) siempre al montar, sin depender de TTCC
        if (initialBootDoneRef.current) return
        initialBootDoneRef.current = true
        info('PostsList', '📍 CALL SOURCE: Initial bootstrap (mount)')
        ;(async () => {
            try {
                await get_wall_version({ silent: false, keepPagination: true, postsToLoad: PAGE_SIZE })
            } catch (e) {
                warn('PostsList', 'initial bootstrap get_wall_version failed (will rely on DB fallbacks later)', (e as any)?.message)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        try {
            const cur = (useInteractionsStore.getState().interactions || {}) as Record<string, any>
            const patched: Record<string, any> = {}
            let changed = false
            for (const [k, v] of Object.entries(cur)) {
                const key = String(k)
                const newV = { ...v, post_id: String(v?.post_id ?? key) }
                patched[key] = newV
                if (k !== key || v?.post_id !== newV.post_id) changed = true
            }
            if (changed && Object.keys(patched).length > 0) {
                setInteractions(patched as any)
            }
        } catch {}
    }, [])

    const isAceptedTermsAndConditions = useTermsAndConditionsStore((state) => state.termsAccepted)
    const acceptTermsAndConditions = useTermsAndConditionsStore((state) => state.acceptTerms)

    const { requestPermissions } = getLocation()
    const { isConnected, hasInternet, connectedStable } = getConection() as any
    const { setAlert, closeAlert } = getAlert()
    const offlineBannerShownRef = useRef(false)
    const wasOfflineRef = useRef(false)
    // Forzar un re-render de la lista al volver al primer plano (Android puede "congelar" vistas)
    const [resumeTick, setResumeTick] = useState(0)
    const appStateRef = useRef(AppState.currentState)
    // Fallback para web cuando no existe NavigationContainer y useIsFocused dispara error via useNavigation.
    let isFocused: boolean = true
    try {
        isFocused = useIsFocused()
    } catch (e) {
        if (Platform.OS !== 'web') {
            throw e
        } else {
            // En web sin navegación asumimos que el componente está "en foco" siempre.
            isFocused = true
            if (process?.env?.NODE_ENV !== 'production') {
                warn('PostsList', 'useIsFocused fallback (no NavigationContainer en web)')
            }
        }
    }

    useEffect(() => {
        postsRef.current = dataPosts as PostItem[]
    }, [dataPosts])

    // Función helper para abrir comentarios de un post
    const openCommentsForPost = useCallback(
        (postId: string | number) => {
            if (Platform.OS !== 'web') return

            try {
                // Buscar el post
                const post = postsRef.current?.find((p) => p._id.toString() === postId.toString())
                if (!post) {
                    warn('PostsList', 'Post not found for opening comments', { postId })
                    return
                }

                // Buscar las interacciones del post desde el store
                const interactions = useInteractionsStore.getState().interactions?.[(post as any)._id]
                const updatedPost = { ...post, interactions }

                // Abrir el sidebar
                openRightSidebar?.('comments', {
                    post: updatedPost,
                    onPostUpdate: (updatedInteractions: any) => {
                        const newInter = { post_id: (post as any)._id, ...interactions, ...updatedInteractions }
                        try {
                            setInteractions({ [(post as any)._id]: newInter })
                        } catch {}
                        try {
                            updateSidebarData?.({ post: { ...post, interactions: newInter } })
                        } catch {}
                    },
                })

                info('PostsList', 'Opened comments sidebar', { postId })
            } catch (error) {
                error('PostsList', 'Error opening comments sidebar', error)
            }
        },
        [openRightSidebar, updateSidebarData, setInteractions],
    )

    // Al volver al primer plano, forzar re-render de FlashList y, si no hay posts visibles, hacer un refresh silencioso
    // ⚠️ NO incluir dataPosts en dependencias para evitar loops infinitos al redimensionar
    useEffect(() => {
        const sub = AppState.addEventListener('change', (next) => {
            try {
                const prev = appStateRef.current
                appStateRef.current = next
                if (/inactive|background/.test(prev) && next === 'active') {
                    setResumeTick((x) => x + 1)
                    // Si por algún motivo quedamos sin datos visibles, rehidratar en silencio preservando paginación
                    try {
                        // Usar la ref actual en lugar de dataPosts del closure
                        const count = Array.isArray(postsRef.current) ? postsRef.current.length : 0
                        if (count === 0) {
                            warn('PostsList', '📍 CALL SOURCE: AppState resume with no posts')
                            const currentPostsCount = (postsContext as any[])?.length || PAGE_SIZE
                            const postsToKeep = Math.max(currentPostsCount, PAGE_SIZE)
                            get_wall_version({ silent: true, keepPagination: true, postsToLoad: postsToKeep })
                        }
                    } catch {}
                }
            } catch {}
        })
        return () => sub?.remove()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // ⚠️ Dependencias vacías - solo configurar listener una vez

    // Aviso persistente en web cuando está offline: "Tus interacciones se sincronizarán al reconectar"
    useEffect(() => {
        try {
            const offline = !(isConnected && hasInternet)
            if (offline && !offlineBannerShownRef.current) {
                offlineBannerShownRef.current = true
                setAlert('Sin conexión. Tus interacciones se sincronizarán al reconectar.', 'white', 'wifi-off', 'rgba(229, 29, 13, 0.9)', true)
                wasOfflineRef.current = true
            }
            if (!offline && offlineBannerShownRef.current) {
                // Al volver la conexión, mostrar mensaje breve de restablecido y cerrar el persistente
                closeAlert()
                offlineBannerShownRef.current = false
                setTimeout(() => {
                    setAlert('Conexión restablecida. Sincronizando…', 'white', 'wifi', 'rgba(0, 158, 77, 0.85)', false)
                }, 150)
                // Re-hidratar interacciones para los posts visibles al reconectar
                try {
                    if (wasOfflineRef.current) {
                        wasOfflineRef.current = false
                        const source = (dataPosts as PostItem[]) || []
                        if (source.length > 0) {
                            fetchInteractions(source).catch(() => {})
                        }
                    }
                } catch {}
            }
        } catch {}
    }, [isConnected, hasInternet])

    // Log global cada vez que cambia el catálogo de reacciones
    useEffect(() => {
        try {
            lazy('Reactions', 'info', () => {
                const names = (allReactions || []).map((r: any) => r?.name).filter(Boolean)
                return ['catalog updated', { len: Array.isArray(allReactions) ? allReactions.length : 0, names }]
            })
        } catch (e) {
            warn('Reactions', 'catalog log error', (e as any)?.message)
        }
    }, [allReactions])
    const snapPoints = ['80%']

    const handle_terms = async (txt: string, version?: string) => {
        console.log('DEBERIA ABRIR ==> xs')
        openBottomSheet(
            <TermsAndConditions termsText={txt} onAccept={acceptTerms} closeSearch={() => closeBottomSheet(true)} version={version as any} />,
            null,
            false,
        )
    }

    // Normaliza user_reaction para el store: acepta array u objeto, y si no viene,
    // intenta derivarlo desde el array de counts (reactions) buscando isSelected
    const normalizeUserReactionForStore = (userReactionRaw: any, countsArr: any[]) => {
        try {
            let urArr: any[] = []
            if (Array.isArray(userReactionRaw)) {
                urArr = userReactionRaw
            } else if (userReactionRaw && typeof userReactionRaw === 'object') {
                urArr = [userReactionRaw]
            }

            // Si no hay user_reaction explícito, intentar derivarlo desde los counts marcados
            if ((!urArr || urArr.length === 0) && Array.isArray(countsArr)) {
                const selected = countsArr.find((c: any) => c?.isSelected)
                if (selected) {
                    urArr = [
                        {
                            reaction_id: selected?.reaction_id ?? selected?._id,
                            _id: selected?._id ?? selected?.reaction_id,
                            name: selected?.name ?? selected?.reaction_name,
                            reaction_name: selected?.name ?? selected?.reaction_name,
                            reaction_index:
                                typeof selected?.index === 'number'
                                    ? selected.index
                                    : typeof selected?.reaction_index === 'number'
                                    ? selected.reaction_index
                                    : 0,
                            isSelected: true,
                        },
                    ]
                }
            }

            // Homogeneizar propiedades y tipos
            urArr = (urArr || [])
                .map((u: any) => ({
                    reaction_id: u?.reaction_id ?? u?._id ?? u?.id,
                    _id: u?._id ?? u?.reaction_id ?? u?.id,
                    name: u?.name ?? u?.reaction_name,
                    reaction_name: u?.reaction_name ?? u?.name,
                    reaction_index: typeof u?.reaction_index === 'number' ? u.reaction_index : typeof u?.index === 'number' ? u.index : 0,
                    isSelected: u?.isSelected === true,
                }))
                .filter(Boolean)

            return urArr
        } catch {
            return []
        }
    }

    const fetchInteractions = async (posts: PostItem[] = []) => {
        try {
            if (isConnected && hasInternet) {
                let authToken: string | null = null
                try {
                    authToken = await getAuthToken('posts_wall')
                } catch (e) {
                    warn('PostsList', 'fetchInteractions: getAuthToken failed')
                }
                if (authToken) {
                    const source = posts.length > 0 ? posts : postsRef.current || []
                    const postIds = source.map((post) => ({ _id: post._id }))
                    const data = await request_get_reactions_wall_according_id({ authToken, post_ids: postIds } as any)
                    if (
                        data?.status === 1 &&
                        data?.single &&
                        Array.isArray(data?.single?.posts_reactions) &&
                        data.single.posts_reactions.length > 0
                    ) {
                        const interactionsById: Record<string, any> = {}
                        data.single.posts_reactions.forEach((int: any) => {
                            const p = source.find((pp: any) => pp._id?.toString() === int.post_id?.toString()) || {}
                            // Normalizar reaction_counter local si viene en string
                            let reactionsLocal = (p as any)?.reaction_counter
                            if (typeof reactionsLocal === 'string') {
                                try {
                                    reactionsLocal = JSON.parse(reactionsLocal)
                                } catch {
                                    reactionsLocal = []
                                }
                            }
                            if (!Array.isArray(reactionsLocal)) reactionsLocal = []
                            const viewsLocal = Number((p as any)?.views) || 0
                            const commentLocal = Number((p as any)?.comment_count) || 0
                            const reactionsMerged = mergeReactionsCounts(reactionsLocal, Array.isArray(int.reactions) ? int.reactions : [])
                            const normalizedUR = normalizeUserReactionForStore(int?.user_reaction, Array.isArray(int.reactions) ? int.reactions : [])
                            const merged = {
                                post_id: String(int.post_id),
                                reactions: reactionsMerged,
                                latest_reactions: Array.isArray(int.latest_reactions) ? int.latest_reactions : [],
                                user_reaction: normalizedUR,
                                comment_count: typeof int.comment_count === 'number' ? int.comment_count : commentLocal,
                                comment_first: int.comment_first ?? (p as any)?.comment_first ?? {},
                                views: typeof int.views === 'number' ? int.views : viewsLocal,
                                _source: 'remote',
                            }
                            const key = String(int.post_id)
                            interactionsById[key] = { ...merged, post_id: key }
                        })
                        setInteractions(interactionsById)
                        return
                    }
                }
            }

            // Fallback: construir interacciones desde datos locales si no hay red, token o respuesta vacía
            const source = posts.length > 0 ? posts : postsRef.current || []
            if (source.length > 0) {
                const interactionsById: Record<string, any> = {}
                for (const p of source) {
                    let reactionsLocal: any = (p as any)?.reaction_counter
                    if (typeof reactionsLocal === 'string') {
                        try {
                            reactionsLocal = JSON.parse(reactionsLocal)
                        } catch {
                            reactionsLocal = []
                        }
                    }
                    if (!Array.isArray(reactionsLocal)) reactionsLocal = []
                    const viewsLocal = Number((p as any)?.views) || 0
                    const commentLocal = Number((p as any)?.comment_count) || 0
                    const key = String((p as any)._id)
                    interactionsById[key] = {
                        post_id: key,
                        reactions: reactionsLocal,
                        latest_reactions: [],
                        user_reaction: normalizeUserReactionForStore(undefined as any, reactionsLocal),
                        comment_count: commentLocal,
                        comment_first: (p as any)?.comment_first ?? {},
                        views: viewsLocal,
                        _source: 'local',
                    }
                }
                setInteractions(interactionsById)
            }
        } catch (error) {
            error('PostsList', 'Error en la solicitud de fetchInteractions:', error)
        } finally {
            setUpdateFlag((prevFlag) => !prevFlag)
        }
    }

    const onRefresh = async () => {
        try {
            setRefreshing(true)
            // Preservar la paginación actual: recargar todos los posts que ya tenemos
            const currentPostsCount = (dataPosts as any[])?.length || PAGE_SIZE
            const postsToKeep = Math.max(currentPostsCount, PAGE_SIZE)
            info('PostsList', '📍 CALL SOURCE: onRefresh - preserving pagination, reloading', postsToKeep, 'posts')

            await get_wall_version({ silent: false, keepPagination: true, postsToLoad: postsToKeep })
                .then(() => {
                    setRefreshing(false)
                })
                .finally(() => {
                    setLoading(false)
                })
                .catch((err) => {
                    error('PostsList', 'onRefresh - get_wall_version', err)
                })
        } catch (error) {
            error('PostsList', 'onRefresh muro', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', async (data) => {
            const ev = (data as any)?.payload?.event
            if (ev === 'signedIn' || ev === 'tokenRefresh' || ev === 'tokenRefresh_succeeded') {
                info('PostsList', '📍 CALL SOURCE: Auth event', ev)
                setTimeout(() => {
                    get_wall_version({ silent: true })
                }, 300)
            }
        })
        return () => unsubscribe()
    }, [])

    const silentRef = useRef(false)
    const getWallInProgressRef = useRef(false)
    const lastGetWallCallTsRef = useRef(0)
    const getWallCallCountRef = useRef(0)

    const get_wall_version = async (opts: { silent?: boolean; keepPagination?: boolean; postsToLoad?: number } = {}) => {
        try {
            // Incrementar contador de llamadas
            getWallCallCountRef.current += 1
            const callNumber = getWallCallCountRef.current

            // Protección contra loops: no ejecutar si ya se está ejecutando
            if (getWallInProgressRef.current) {
                warn('PostsList', `⚠️ get_wall_version #${callNumber} BLOCKED - already in progress`)
                return
            }

            // Protección adicional: no ejecutar más de una vez cada 500ms
            const now = Date.now()
            const timeSinceLastCall = now - lastGetWallCallTsRef.current
            if (timeSinceLastCall < 500) {
                warn('PostsList', `⚠️ get_wall_version #${callNumber} THROTTLED - called too soon (${timeSinceLastCall}ms ago)`)
                // Si se está llamando muy frecuentemente, puede ser un loop
                if (getWallCallCountRef.current > 10) {
                    error('PostsList', `🚨 LOOP DETECTED - get_wall_version called ${getWallCallCountRef.current} times! Stopping execution.`)
                    // Reset contador después de 5 segundos
                    setTimeout(() => {
                        getWallCallCountRef.current = 0
                    }, 5000)
                    return
                }
                return
            }

            info('PostsList', `✅ get_wall_version #${callNumber} STARTING`, {
                silent: opts?.silent,
                keepPagination: opts?.keepPagination,
                postsToLoad: opts?.postsToLoad,
                timeSinceLastCall,
            })

            // Agregar stack trace para depuración de loops
            if (callNumber > 3 && Platform.OS === 'web') {
                console.trace(`🔍 get_wall_version stack trace for call #${callNumber}`)
            }

            getWallInProgressRef.current = true
            lastGetWallCallTsRef.current = now

            const silent = opts?.silent === true
            const keepPagination = opts?.keepPagination === true
            const postsToLoad = opts?.postsToLoad || PAGE_SIZE

            silentRef.current = silent
            if (!silent) {
                setLoading((prev) => ((dataPosts as PostItem[]) && (dataPosts as PostItem[]).length > 0 ? false : true))
            }

            // Solo resetear la página si NO estamos preservando la paginación
            if (!silent && !keepPagination) {
                setPage(0)
            }

            // Rama rápida Web: usar servicio web ya cacheado (evita overhead de lógica móvil)
            if (Platform.OS === 'web') {
                try {
                    // Carga dinámica para evitar bundling innecesario
                    // eslint-disable-next-line @typescript-eslint/no-var-requires
                    const webSrv = require('../../services/web/postsWebService')
                    const authToken = (await getAuthToken('posts_web_fetch_wall')) || ''
                    const response = await webSrv.fetchWallWeb({ authToken, page: '0', size: '100', version: null, force: !silent })
                    console.log('Respuesta de webSrv.fetchWallWeb:', response)
                    // TODO: habitar modal alias en web. revisar porq es un formulario que funciona con sqlite y en web no funcionan
                    // const formData = response?.form || null
                    // console.log('WEB FORM DATA RECEIVED FROM API:', formData)
                    // setForm(formData)
                    // if (formData != null && alreadySentForm === false) {
                    //     openFormModal(formData)
                    // }

                    const posts = webSrv.getCachedPosts()
                    const labelsFromApi = webSrv.getCachedLabels()
                    const reactionsFromApi = webSrv.getCachedReactions?.() || []

                    debug('PostsList', 'Data from webSrv', {
                        posts: posts?.length || 0,
                        labels: labelsFromApi?.length || 0,
                        reactions: reactionsFromApi?.length || 0,
                        selectedLabel,
                    })
                    ;(setPostsContext as any)(posts)
                    // Ajustar label seleccionado
                    const effectiveLabel = selectedLabel == null ? (-2 as LabelIdT) : selectedLabel
                    let visible: any[] = []
                    if (effectiveLabel === -1) {
                        visible = posts.filter((p) => p.status_favourite === 1).slice(0, postsToLoad)
                    } else if (effectiveLabel === -2) {
                        visible = posts.slice(0, postsToLoad)
                    } else {
                        visible = posts.filter((p) => (p.labels || []).some((l: any) => l._id === effectiveLabel)).slice(0, postsToLoad)
                    }
                    info('PostsList', 'get_wall_version web - loaded', { loaded: visible.length, requested: postsToLoad })
                    await fetchInteractions(visible)

                    // Asegurar catálogo de reacciones global (single.reactions)
                    try {
                        if (Array.isArray(reactionsFromApi) && reactionsFromApi.length > 0) {
                            debug('Reactions', 'setallReactions (WEB cache single.reactions)', { len: reactionsFromApi.length })
                            setallReactions(reactionsFromApi as any)
                            if (typeof window !== 'undefined') {
                                ;(window as any).__globalReactions = reactionsFromApi
                            }
                            // Persistir catálogo en el servicio web (memoria/localStorage) para fallback offline
                            try {
                                await Promise.all(
                                    (reactionsFromApi as any[]).map(async (r) => {
                                        try {
                                            await (PostsServices as any).addReaction?.(r)
                                        } catch {}
                                    }),
                                )
                            } catch {}
                        }
                    } catch {}

                    // Solo actualizar posts si NO es silent O si los datos realmente cambiaron
                    if (!silent) {
                        debug('PostsList', 'NOT silent - updating posts')
                        setDataPosts(visible)
                    } else {
                        // En modo silent, solo actualizar si hay cambios reales
                        const currentPostIds = (dataPosts as any[])?.map((p: any) => p._id).join(',') || ''
                        const newPostIds = visible.map((p: any) => p._id).join(',')
                        if (currentPostIds !== newPostIds) {
                            debug('PostsList', 'Silent mode - posts changed, updating')
                            setDataPosts(visible)
                        } else {
                            debug('PostsList', 'Silent mode - no changes, skipping update')
                        }
                    }

                    // Fusionar labels base (favourite / all) con las de API si no existen
                    setLabels((prev) => {
                        const baseIds = new Set(prev.map((l) => l._id))
                        const toAdd = labelsFromApi.filter((l) => !baseIds.has(l._id))
                        return [...prev, ...toAdd]
                    })
                    // ✅ NO establecer setLoading(false) aquí - el useEffect lo hará cuando dataPosts esté listo
                    return // Fin rama web
                } catch (e) {
                    warn('PostsList', 'get_wall_version web fallback error, continuará con flujo nativo simplificado', e as any)
                }
            }
            const [fetchedPosts, reactions] = await Promise.all([PostsServices.getAllPosts(0, -1), PostsServices.getAllReactions(), fetchLabels()])

            if (!silent && fetchedPosts && fetchedPosts.length > 0) {
                // ✅ Importante: asegurar catálogo desde DB local ANTES de renderizar posts
                try {
                    if (Array.isArray(reactions) && reactions.length > 0) {
                        debug('Reactions', 'setallReactions (NATIVE local DB)', { len: reactions.length })
                        setallReactions(reactions as any)
                    }
                } catch {}
                ;(setPostsContext as any)(fetchedPosts)
                try {
                    info('PostsList', 'Emitting app:hasLocalPosts (found local cached posts)')
                    eventEmitter.emit('app:hasLocalPosts')
                } catch {}

                if (selectedLabel === -2 || selectedLabel == null) {
                    const posts = (getPostsByLabel as any)(0, postsToLoad, selectedLabel as any, fetchedPosts as any)
                    info('PostsList', 'get_wall_version native - loaded', { loaded: posts.length, requested: postsToLoad })
                    precalcAndCacheHeights(posts, width - 16)
                    await fetchInteractions(posts)
                    setDataPosts(posts)
                } else if (selectedLabel === -1) {
                    const posts = (getPostsFavourite as any)()
                    info('PostsList', 'get_wall_version native - loaded favourites', { loaded: posts.length })
                    precalcAndCacheHeights(posts, width - 16)
                    await fetchInteractions(posts)
                    setDataPosts(posts)
                } else {
                    const posts = (getPostsByLabel as any)(0, postsToLoad, selectedLabel as any, fetchedPosts as any)
                    info('PostsList', 'get_wall_version native - loaded', { loaded: posts.length, requested: postsToLoad })
                    precalcAndCacheHeights(posts, width - 16)
                    await fetchInteractions(posts)
                    setDataPosts(posts)
                }
            }

            const effectivelyOnline = (isConnected && hasInternet) || Platform.OS === 'ios'
            if (effectivelyOnline) {
                // Primero, intentar con getAuthToken que incluye circuit breaker
                let authToken: string | null = null
                try {
                    authToken = await getAuthToken('posts_wall')
                } catch {}
                // Evitar tocar fetchAuthSession si no hay usuario/tokens aún (reduce NotAuthorized en Android Expo Go)

                let retries = 2
                let timeout = 10
                if ((fetchedPosts as any[])?.length > 0) {
                    retries = 0
                    timeout = 4
                }
                if (authToken) {
                    const version = await UserServices.getGlobalVersion('posts')
                    await fetchAndSavePosts(authToken, version, retries, timeout).catch((error: any) => {
                        error('PostsList', 'get_wall_version', error)
                    })
                } else {
                    const now = Date.now()
                    const info = authRetryInfoRef.current
                    // Reset ventana si pasó > 2 minutos
                    if (now - info.lastTs > 120000) {
                        info.count = 0
                        info.backoffMs = 1500
                    }
                    if (info.count < 3) {
                        info.count += 1
                        info.lastTs = now
                        warn('PostsList', '📍 CALL SOURCE: Auth token retry', { c: info.count, ms: info.backoffMs })
                        const wait = info.backoffMs
                        // Exponenciar hasta ~12s
                        info.backoffMs = Math.min(info.backoffMs * 2, 12000)
                        setTimeout(() => {
                            get_wall_version({ silent: true })
                        }, wait)
                    } else {
                        // No reintentar más por ahora; quedamos con DB local
                        warn('PostsList', 'get_wall_version: max auth retries reached; staying on local data')
                    }
                }
            }
        } catch (error) {
            error('PostsList', 'get_wall_version error', error)
        } finally {
            if (!silentRef.current) setLoading(false)
            getWallInProgressRef.current = false
        }
    }

    const fetchAndSavePosts = async (authToken: string, version: any, retries: number, timeout: number) => {
        if (globalFetchAndSavePostsInProgress) {
            warn('PostsList', '⚠️ fetchAndSavePosts BLOCKED - already in progress (GLOBAL)')
            return {
                status: 0,
                single: { data: [], reactions: [], data_version: null },
            }
        }

        globalFetchAndSavePostsInProgress = true
        info('PostsList', '✅ fetchAndSavePosts STARTING (GLOBAL)')

        try {
            const pageSize = size
            let page = 0
            let allPosts: PostItem[] = []
            let allReactions: any[] = []
            let dataVersion: any = null

            const initialResponse = await request_get_wall({
                authToken,
                version,
                page: page.toString(),
                size: pageSize.toString(),
                retries: retries,
                timeout: timeout,
            })

            const formData = initialResponse?.single?.form || null
            setForm(formData)
            if (formData != null && alreadySentForm === false) {
                openFormModal(formData)
            }

            if (
                initialResponse &&
                initialResponse.status === 1 &&
                initialResponse.single &&
                initialResponse.single.data &&
                initialResponse.single.data.length > 0
            ) {
                const { data, reactions, data_version } = initialResponse.single
                // Limpiar tablas solo si realmente cambia la versión remota
                try {
                    if (data_version && data_version !== version) {
                        const currentStoredVersion = await UserServices.getGlobalVersion('posts')

                        if (currentStoredVersion === data_version) {
                            info('PostsList', '⚠️ Version already updated by another instance, skipping delete')
                            // No borrar, solo agregar/actualizar posts
                        } else {
                            info('PostsList', `🧹 Limpio tablas de posts porque cambió la versión: local=${version} -> remota=${data_version}`)
                            await PostsServices.deleteAllTablesPosts()
                            globalFirstBatchReadyEmitted = false
                        }
                    }
                } catch (e: any) {
                    warn('PostsList', 'deleteAllTablesPosts fallo (continuo igual):', e?.message)
                }
                allPosts = [...allPosts, ...data]

                // Sólo actualizar UI inmediata si NO es silent (primer render) o si la versión cambió
                const shouldUpdateImmediateUI = !silentRef.current || (data_version && data_version !== version)
                if (shouldUpdateImmediateUI) {
                    ;(setPostsContext as any)(allPosts)
                    try {
                        const firstBatch = (getPostsByLabel as any)(0, PAGE_SIZE, selectedLabel as any, allPosts as any)
                        setDataPosts(firstBatch)
                        try {
                            // Solo emitir firstBatchReady una vez por sesión
                            if (!globalFirstBatchReadyEmitted) {
                                info('PostsList', 'Emitting app:firstBatchReady (posts first batch ready)')
                                eventEmitter.emit('app:firstBatchReady')
                                globalFirstBatchReadyEmitted = true
                            }
                        } catch {}
                    } catch (e) {
                        warn('PostsList', 'fetchAndSavePosts: could not set first batch UI', (e as any)?.message)
                    }
                }
                allReactions = reactions
                // ✅ Catálogo global de reacciones desde la API (single.reactions)
                try {
                    if (Array.isArray(reactions) && reactions.length > 0) {
                        debug('Reactions', 'setallReactions (NATIVE get_wall FIRST PAGE)', { len: reactions.length })
                        setallReactions(reactions as any)
                        if (Platform.OS === 'web' && typeof window !== 'undefined') {
                            ;(window as any).__globalReactions = reactions
                        }
                    }
                } catch {}
                dataVersion = data_version

                await PostsServices.addAllPosts(data)

                try {
                    for (const reaction of reactions) {
                        await PostsServices.addReaction(reaction)
                    }
                } catch (error) {
                    error('PostsList', 'Error al añadir posts o reacciones:', error)
                }

                page++
            } else {
                return {
                    status: 0,
                    single: { data: [], reactions: [], data_version: null },
                }
            }

            const initialData = {
                status: 1,
                single: { data: allPosts, reactions: allReactions, data_version: dataVersion },
            }

            ;(async () => {
                while (true) {
                    if (!silentRef.current) changeLoading(true)
                    const response = await request_get_wall({
                        authToken,
                        version,
                        page: page.toString(),
                        size: pageSize.toString(),
                    })

                    if (response.status !== 1 || !response.single || !response.single.data || response.single.data.length === 0) {
                        break
                    }

                    const { data, reactions } = response.single
                    allPosts = [...allPosts, ...data]
                    allReactions = reactions
                    // ✅ Mantener actualizado catálogo global (normalmente es estable)
                    try {
                        if (Array.isArray(reactions) && reactions.length > 0) {
                            debug('Reactions', 'setallReactions (NATIVE get_wall NEXT PAGE)', { len: reactions.length })
                            setallReactions(reactions as any)
                        }
                    } catch {}
                    await PostsServices.addAllPosts(data)
                    page++
                }

                await saveVersionPosts(dataVersion)

                info('PostsList', 'All pages fetched and saved')
                if (!silentRef.current) changeLoading(false)
                // Sólo refrescar UI al finalizar si NO es silent o si hubo cambio de versión
                const shouldRefreshUI = !silentRef.current || (dataVersion && dataVersion !== version)
                if (shouldRefreshUI) {
                    ;(setPostsContext as any)(allPosts)
                    try {
                        const list = (getPostsByLabel as any)(0, PAGE_SIZE, selectedLabel as any, allPosts as any)
                        setDataPosts(list)
                    } catch (e) {
                        warn('PostsList', 'fetchAndSavePosts: could not refresh UI after full fetch', (e as any)?.message)
                    }
                }
                try {
                    info('PostsList', 'Emitting app:fullySynced (all posts fetched & saved)')
                    eventEmitter.emit('app:fullySynced')
                } catch {}
                // Evitar refrescos de UI (labels/reactions) innecesarios en silent sin cambios de versión
                const shouldRefreshUI2 = !silentRef.current || (dataVersion && dataVersion !== version)
                if (shouldRefreshUI2) {
                    await fetchLabels()
                    await fetchReactions()
                }
                // Prefetch imágenes si versión cambió (y sólo una vez por versión)
                if (!isWeb() && dataVersion && dataVersion !== lastPrefetchedVersionRef.current) {
                    lastPrefetchedVersionRef.current = dataVersion
                    prefetchPostImages(allPosts)
                    // Prefetch avatar del usuario y limpieza de avatares obsoletos
                    ;(async () => {
                        try {
                            const user = await UserServices.getUser()
                            const avatarKey = (user as any)?.avatar || (user as any)?.avatar_image || ''
                            if (avatarKey && typeof avatarKey === 'string') {
                                // Intentar resolver URL pública vía Amplify si es una key S3
                                let avatarUrl: string | null = null
                                try {
                                    const { getImageStogeUrl } = require('../imageUploader/ImageStorage')
                                    avatarUrl = await getImageStogeUrl(avatarKey)
                                } catch {}
                                // Si falló, asumir que avatarKey ya es una URL completa
                                if (!avatarUrl && /^https?:\/\//i.test(avatarKey)) avatarUrl = avatarKey
                                if (avatarUrl) {
                                    await downloadAvatarUser(avatarUrl, avatarKey)
                                    await pruneAvatars([avatarKey])
                                }
                            } else {
                                // Si no hay avatar, limpiar todo el directorio
                                await pruneAvatars([])
                            }
                        } catch (e) {
                            warn('PostsList', 'prefetch avatar error:', (e as any)?.message)
                        }
                    })()
                }
            })()

            return initialData
        } catch (err) {
            changeLoading(false)
            error('PostsList', 'cathch en fetchAndSavePosts', err)
            throw err
        } finally {
            globalFetchAndSavePostsInProgress = false
            info('PostsList', '🏁 fetchAndSavePosts COMPLETED - global lock released')
        }
    }

    const saveVersionPosts = async (dataVersion) => {
        try {
            if (dataVersion != null) {
                await UserServices.updateGlobalVersion('posts', dataVersion)
            }
        } catch (e) {
            warn('PostsList', 'updateGlobalVersion(posts) failed', (e as any)?.message)
        }
    }

    // Desactivado: refresco automático cada 2 minutos
    // useEffect(() => {
    //   if (!isFocused) return
    //   const interval = setInterval(() => {
    //     get_wall_version({ silent: true })
    //   }, 120000)
    //   return () => clearInterval(interval)
    // }, [isFocused])

    const fetchLabels = async () => {
        try {
            const raw = await PostsServices.getAllLabels()
            try {
                debug('Labels', 'raw label rows count', raw?.length || 0)
                if (raw && raw.length) {
                    lazy('Labels', 'debug', () => ['first raw row sample', JSON.stringify(raw[0]).slice(0, 240)])
                }
            } catch {}

            // Si no hay labels y tenemos posts, es un error - mantener los labels actuales
            if ((!raw || raw.length === 0) && postsContext?.length > 0) {
                // No actualizar labels si ya tenemos algunos
                if (labels.length > 2) {
                    return
                }
            }

            const sanitized = (raw || [])
                .map((l: any, idx: number) => {
                    if (!l) return null
                    const _id = l._id ?? l.id ?? l.label_id ?? `lbl_${idx}`
                    const name = l.name ?? l.label ?? l.title ?? (typeof l === 'string' ? l : 'Etiqueta')
                    return { ...l, _id, name }
                })
                .filter(Boolean)
            const seen = new Set()
            const unique: any[] = []
            for (const l of sanitized) {
                const key = `${l._id}::${l.name}`
                if (!seen.has(key)) {
                    seen.add(key)
                    unique.push(l)
                }
            }
            // Estadísticas de labels
            try {
                const freqMap: Record<string, number> = {}
                unique.forEach((l) => {
                    freqMap[l.name] = (freqMap[l.name] || 0) + 1
                })
                const top = Object.entries(freqMap)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 15)
                debug('Labels', 'unique labels', { count: unique.length, top15: top })
            } catch {}
            const all_labels: LabelItem[] = [
                { _id: -1, name: '', isFavourite: true },
                { _id: null, name: languageFile.all, isSelected: selectedLabel === -2 || selectedLabel == null },
                ...unique.map((l) => ({ ...l, isSelected: l._id === selectedLabel })),
            ]

            // Solo actualizar si tenemos labels o si es la primera carga
            if (all_labels.length > 2 || labels.length === 0) {
                setLabels(all_labels)
            }
        } catch (err) {
            error('PostsList', 'Error fetching labels:', err)
            // No lanzar error, mantener labels actuales
        }
    }

    const fetchReactions = async () => {
        try {
            const reactions = await PostsServices.getAllReactions()
            setallReactions(reactions)
        } catch (error) {
            error('PostsList', 'Error fetching labels:', error)
        }
    }

    const fetchPosts = async (pageNumber = 0, label_id: number | null = -2) => {
        try {
            setLoading(true)
            const fetchedPosts = (getPostsByLabel as any)(pageNumber, PAGE_SIZE, label_id as any)
            if (fetchedPosts.length > 0) {
                await fetchInteractions(fetchedPosts)
                ;(setDataPosts as any)(fetchedPosts)
                setNewlyAddedIds((prev) => {
                    const next = new Set<string | number>(prev)
                    fetchedPosts.forEach((p: any) => next.add(p._id))
                    return next
                })
            }
            changeLabel(-2 as any)
            setLoading(false)
        } catch (error) {
            error('PostsList', 'fetchPosts - ERROR', error)
        }
    }

    useEffect(() => {
        const fetchTerms = async () => {
            await getTerms()
            // Offline fallback: si no hay conexión, mostrar último TTCC global si no está aceptado
            if (!isConnected || !hasInternet) {
                try {
                    const cached = await StorageAdapter.getItem(ASYNC_STORAGE_KEYS.TERMS_AND_CONDITIONS_LAST_GLOBAL)
                    if (cached) {
                        const parsed = JSON.parse(cached)
                        const ver = String(parsed?.version ?? '')
                        const globalKey = `${ASYNC_STORAGE_KEYS.TERMS_AND_CONDITIONS_ACCEPTED}:global:${ver}`
                        const accepted = await StorageAdapter.getItem(globalKey)
                        if (accepted !== 'true' && parsed?.terms) {
                            await handle_terms(String(parsed.terms))
                        }
                    }
                } catch {}
            }
        }
        fetchTerms()
    }, [])

    // Reintentar TTCC al recuperar conexión
    // useEffect(() => {
    //     const unsubscribe = NetInfo.addEventListener((state) => {
    //         try {
    //             if (state.isConnected) {
    //                 getTerms()
    //             }
    //         } catch {}
    //     })
    //     return () => {
    //         try {
    //             unsubscribe && unsubscribe()
    //         } catch {}
    //     }
    // }, [])

    // Ref para evitar múltiples ejecuciones del efecto de carga inicial
    const initialFetchDoneRef = useRef(false)

    useEffect(() => {
        info('PostsList', '🔄 Main fetch effect triggered', {
            isAceptedTermsAndConditions,
            initialFetchDone: initialFetchDoneRef.current,
            dataPostsLength: (dataPosts as any[])?.length || 0,
        })

        const fetchData = async () => {
            // Protección contra múltiples ejecuciones
            if (initialFetchDoneRef.current) {
                debug('PostsList', 'Initial fetch already done, skipping')
                return
            }

            // Si ya hay posts cargados, no volver a cargar
            if ((dataPosts as any[])?.length > 0) {
                info('PostsList', 'Skipping initial load - posts already loaded:', (dataPosts as any[])?.length)
                initialFetchDoneRef.current = true
                // ✅ Asegurar que el catálogo de reacciones esté disponible aunque saltemos la recarga
                try {
                    if (!Array.isArray(allReactions) || allReactions.length === 0) {
                        if (Platform.OS === 'web') {
                            try {
                                // eslint-disable-next-line @typescript-eslint/no-var-requires
                                const webSrv = require('../../services/web/postsWebService')
                                const cached = webSrv.getCachedReactions?.() || []
                                if (Array.isArray(cached) && cached.length > 0) {
                                    debug('Reactions', 'setallReactions (WEB cache on return)', { len: cached.length })
                                    setallReactions(cached)
                                }
                            } catch {}
                        }
                        if (!Array.isArray(allReactions) || allReactions.length === 0) {
                            try {
                                const dbReactions = await PostsServices.getAllReactions()
                                if (Array.isArray(dbReactions) && dbReactions.length > 0) {
                                    debug('Reactions', 'setallReactions (DB fallback on return)', { len: dbReactions.length })
                                    setallReactions(dbReactions as any)
                                }
                            } catch {}
                        }
                    }
                } catch {}
                setLoading(false)
                return
            }

            // Marcar como ejecutado antes de iniciar la carga
            initialFetchDoneRef.current = true

            info('PostsList', '📍 CALL SOURCE: Terms accepted - main fetch')
            // Cargar primero get_wall_version para obtener single.reactions (catálogo autorizado)
            await get_wall_version()
            // Luego cargar labels y permisos (fetchPosts es redundante ya que get_wall_version actualiza los posts)
            await Promise.all([fetchLabels(), requestPermissions()])
            // Fallback: si aún no hay catálogo global (offline), cargar desde DB local
            if (!Array.isArray(allReactions) || allReactions.length === 0) {
                await fetchReactions()
            }
        }
        if (isAceptedTermsAndConditions == true) {
            fetchData()
        }
    }, [isAceptedTermsAndConditions])

    // ✅ Refuerzo: al ganar foco, si hay posts pero reacciones vacías, intentar rellenarlas desde cache/DB
    useEffect(() => {
        if (!isFocused) return
        ;(async () => {
            try {
                if (((dataPosts as any[])?.length || 0) > 0 && (!Array.isArray(allReactions) || allReactions.length === 0)) {
                    if (Platform.OS === 'web') {
                        try {
                            // eslint-disable-next-line @typescript-eslint/no-var-requires
                            const webSrv = require('../../services/web/postsWebService')
                            const cached = webSrv.getCachedReactions?.() || []
                            if (Array.isArray(cached) && cached.length > 0) {
                                debug('Reactions', 'setallReactions (WEB cache on focus)', { len: cached.length })
                                setallReactions(cached)
                                return
                            }
                        } catch {}
                    }
                    try {
                        const dbReactions = await PostsServices.getAllReactions()
                        if (Array.isArray(dbReactions) && dbReactions.length > 0) {
                            debug('Reactions', 'setallReactions (DB fallback on focus)', { len: dbReactions.length })
                            setallReactions(dbReactions as any)
                        }
                    } catch {}
                }
            } catch {}
        })()
    }, [isFocused])

    // ✅ Desactivar loading cuando:
    // 1. dataPosts tiene datos, O
    // 2. Se completó la carga inicial (get_wall_version respondió, aunque sea con array vacío)
    useEffect(() => {
        if ((dataPosts as any[])?.length > 0 && loading) {
            debug('PostsList', 'Data loaded - disabling loading state')
            setLoading(false)
        }
        // Si dataPosts es un array vacío pero ya no está en estado inicial (postsContext existe o fue procesado)
        // también desactivar loading después de un momento
        if ((dataPosts as any[])?.length === 0 && Array.isArray(dataPosts) && loading && !silentRef.current) {
            // Dar un momento para que se complete el flujo
            const timer = setTimeout(() => {
                debug('PostsList', 'No data returned - disabling loading state')
                setLoading(false)
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [dataPosts, loading])

    const handleEndReached = async () => {
        // debug suprimido: handleEndReached called

        if (selectedLabel === -1 || loading) {
            // debug suprimido: blocked selectedLabel/loading
            return
        }
        if (isFetchingMoreRef.current) {
            // debug suprimido: already fetching
            return
        }
        isFetchingMoreRef.current = true
        try {
            const nextPage = (page ?? 0) + 1
            // debug suprimido: fetching page
            const fetchedPosts = (getPostsByLabel as any)(nextPage, PAGE_SIZE, selectedLabel as any)
            // Precalcular alturas (media y total) antes de incorporarlos, para evitar saltos al enfocar/paginar
            try {
                // Usar ancho de columna real según layout activo para estimaciones coherentes
                let availW = width - 16
                if (Platform.OS === 'web' && isCssColumnsMasonryActive && dynamicColumns > 0) {
                    const gap = 12 // columnGap
                    const totalGaps = Math.max(0, dynamicColumns - 1) * gap
                    availW = Math.max(80, Math.floor((availW - totalGaps) / dynamicColumns))
                }
                precalcAndCacheHeights(fetchedPosts, availW)
            } catch {}
            // debug suprimido: fetchedPosts count
            await fetchInteractions(fetchedPosts)
            if (fetchedPosts.length > 0) {
                // debug suprimido: appending new posts
                if (nextPage > 0) {
                    ;(setDataPosts as any)((prev: any[]) => {
                        const merged = [...prev, ...fetchedPosts]
                        // debug suprimido: total posts
                        setNewlyAddedIds((prevIds) => {
                            const nextIds = new Set<string | number>(prevIds)
                            fetchedPosts.forEach((p: any) => nextIds.add(p._id))
                            return nextIds
                        })
                        return merged
                    })
                } else {
                    ;(setDataPosts as any)(fetchedPosts)
                    setNewlyAddedIds((prevIds) => {
                        const nextIds = new Set<string | number>(prevIds)
                        fetchedPosts.forEach((p: any) => nextIds.add(p._id))
                        return nextIds
                    })
                }
                setPage(nextPage)
                // debug suprimido: page updated
            } else {
                // debug suprimido: no more posts
            }
        } finally {
            isFetchingMoreRef.current = false
            // debug suprimido: handleEndReached completed
        }
    }

    const handleSelectLabel = async (labelId: LabelIdT | null) => {
        if (labelId !== selectedLabel) {
            setPage(0)
            autoLoadCountRef.current = 0 // Resetear contador de auto-carga
            hasCheckedInitialLoadRef.current = false // Resetear flag de check inicial
            changeLabel(labelId as any)
            setSelectedLabel((labelId as LabelIdT) ?? -2)

            setLabels((prevLabels) => prevLabels.map((label) => ({ ...label, isSelected: label._id === labelId })))
            // const posteos = getPostsByLabel(0, PAGE_SIZE, (labelId as LabelIdT) ?? -2)
            const posteos = getPostsByLabel(0, 10, labelId)
            await fetchInteractions(posteos)
            setTimeout(() => {
                setLoading(false)
            }, 1500)
            // ;(setDataPosts as any)(posteos)
            setDataPosts(posteos)
            mainViewRef.current?.scrollToOffset?.({ offset: 0, animated: true })
        }
    }

    const onClickFavourite = async (labelId: LabelIdT) => {
        try {
            if (labelId !== selectedLabel) {
                setPage(0)
                autoLoadCountRef.current = 0 // Resetear contador de auto-carga
                hasCheckedInitialLoadRef.current = false // Resetear flag de check inicial
                setSelectedLabel((labelId as LabelIdT) ?? -2)
                setLabels((prevLabels) => prevLabels.map((label) => ({ ...label, isSelected: label._id === labelId })))

                const posts = isWeb() ? getFavouritesWeb() : await PostsServices.getFavouritePosts()
                // console.log('FAVOURITE POSTS:', posts)
                await fetchInteractions(posts)
                const reactions = await PostsServices.getAllReactions()
                setallReactions(reactions)
                setDataPosts(posts)
                setTimeout(() => {
                    setLoading(false)
                }, 1500)
                mainViewRef.current?.scrollToOffset?.({ offset: 0, animated: true })
            }
            
        } catch (error) {
            console.error('onClickFavourite error:', error)
        }
    }

    const getFavouritesWeb = () => {
        try {
            const posts = [...postsContext]
            const favouritePosts = posts.filter((post) => {
                return post.status_favourite === 1
            })
            return favouritePosts
        } catch (error) {
           console.error('getFavouritesWeb error:', error)
           return [] 
        }
    }

    const scrollToPost = async (postId: string | number) => {
        try {
            // Verificar si ya estamos cargando este mismo post
            if (isLoadingProgressivelyRef.current && lastProgressiveLoadPostIdRef.current === postId.toString()) {
                info('PostsList', 'Already loading this post progressively, skipping', { postId })
                return
            }

            const current = postsRef.current || []
            const postIndex = current.findIndex((post) => post._id.toString() === postId.toString())

            if (postIndex !== -1) {
                // Limpiar el postID porque encontramos el post
                setPostID(null)

                // En web, dependiendo del modo de renderizado, usar diferente estrategia de scroll
                if (Platform.OS === 'web') {
                    // Función helper para intentar hacer scroll
                    const attemptScroll = (retries = 0): boolean => {
                        const maxRetries = 5

                        // Buscar el elemento del post en el DOM
                        const postElement = document.querySelector(`[data-post-id="${postId}"]`)
                        if (postElement) {
                            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            info('PostsList', 'Scrolled to post using scrollIntoView', { postId, retries })
                            // Abrir comentarios después del scroll
                            setTimeout(() => {
                                openCommentsForPost(postId)
                            }, 600)
                            return true
                        }

                        // Si no encontramos el elemento y aún tenemos reintentos, intentar de nuevo
                        if (retries < maxRetries) {
                            setTimeout(() => attemptScroll(retries + 1), 200)
                            return false
                        }

                        // Fallback: scroll aproximado usando refs
                        if (masonryScrollRef.current) {
                            const estimatedY = postIndex * 400
                            masonryScrollRef.current.scrollTo({ y: estimatedY, animated: true })
                            info('PostsList', 'Scrolled to post using masonryScrollRef (fallback)', { postId, estimatedY })
                            // Abrir comentarios en fallback
                            setTimeout(() => {
                                openCommentsForPost(postId)
                            }, 600)
                            return true
                        }

                        warn('PostsList', 'Could not scroll to post after retries', { postId, retries })
                        return false
                    }

                    // Esperar un frame para asegurar que el DOM esté actualizado
                    await new Promise((resolve) => setTimeout(resolve, 100))
                    attemptScroll()
                    return
                }

                // Para mobile, usar scrollToIndex de FlashList
                mainViewRef.current?.scrollToIndex?.({ index: postIndex, animated: true })
                return
            }

            // Si el post no está en la lista actual, buscarlo en el contexto
            const ctxIndex = (postsContext as PostItem[]).findIndex((p) => p._id.toString() === postId.toString())
            if (ctxIndex !== -1) {
                // Limpiar el postID porque encontramos el post
                setPostID(null)

                const newPost = (postsContext as PostItem[])[ctxIndex]
                await fetchInteractions([newPost])
                setDataPosts((prev: any) => {
                    const updated = [...(prev || []), newPost]
                    postsRef.current = updated
                    setNewlyAddedIds(new Set([newPost._id]))
                    setTimeout(() => {
                        if (Platform.OS === 'web') {
                            const attemptScroll = (retries = 0) => {
                                const postElement = document.querySelector(`[data-post-id="${postId}"]`)
                                if (postElement) {
                                    postElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                    // Abrir comentarios después del scroll
                                    setTimeout(() => {
                                        openCommentsForPost(postId)
                                    }, 600)
                                } else if (retries < 5) {
                                    setTimeout(() => attemptScroll(retries + 1), 200)
                                }
                            }
                            attemptScroll()
                        } else {
                            mainViewRef.current?.scrollToIndex?.({ index: updated.length - 1, animated: true })
                        }
                    }, 300)
                    return updated
                })
                return
            }

            // Si no está en el contexto tampoco, necesitamos cargar más posts del backend
            // Esto pasa cuando llegas por push notification y el post es antiguo
            info('PostsList', 'Post not found locally, loading more posts progressively', { postId })

            // Marcar que estamos cargando progresivamente este post
            isLoadingProgressivelyRef.current = true
            lastProgressiveLoadPostIdRef.current = postId.toString()

            // Función recursiva para cargar más posts hasta encontrar el que buscamos
            const loadUntilFound = async (currentBatch = 1, maxBatches = 10): Promise<boolean> => {
                const postsToLoad = PAGE_SIZE * currentBatch
                info('PostsList', `Loading batch ${currentBatch}, total posts: ${postsToLoad}`, { postId })

                await get_wall_version({ silent: true, keepPagination: false, postsToLoad })

                // Esperar un poco para que se actualice la lista
                await new Promise((resolve) => setTimeout(resolve, 500))

                // Verificar si el post ya está cargado
                const current = postsRef.current || []
                const foundIndex = current.findIndex((post) => post._id.toString() === postId.toString())

                if (foundIndex !== -1) {
                    info('PostsList', `Post found after loading ${currentBatch} batches`, { postId, foundIndex })
                    return true
                }

                // Si no lo encontramos y aún tenemos intentos, cargar más
                if (currentBatch < maxBatches) {
                    return loadUntilFound(currentBatch + 1, maxBatches)
                }

                // Si llegamos aquí, no pudimos encontrar el post
                warn('PostsList', 'Post not found after loading maximum batches', { postId, maxBatches })
                return false
            }

            const found = await loadUntilFound()

            // Liberar el flag de carga progresiva
            isLoadingProgressivelyRef.current = false
            lastProgressiveLoadPostIdRef.current = null

            if (!found) {
                error('PostsList', 'Could not find post after loading all batches', { postId })
                // Limpiar postID si no se encontró
                setPostID(null)
                return
            }

            // Limpiar postID porque encontramos el post
            setPostID(null)

            // Después de cargar, intentar hacer scroll al post
            setTimeout(() => {
                const current = postsRef.current || []
                const postIndex = current.findIndex((post) => post._id.toString() === postId.toString())

                if (postIndex !== -1) {
                    if (Platform.OS === 'web') {
                        const attemptScroll = (retries = 0) => {
                            const postElement = document.querySelector(`[data-post-id="${postId}"]`)
                            if (postElement) {
                                postElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                info('PostsList', 'Scrolled to post after progressive loading', { postId })
                                // Abrir comentarios después del scroll
                                setTimeout(() => {
                                    openCommentsForPost(postId)
                                }, 600)
                            } else if (retries < 10) {
                                setTimeout(() => attemptScroll(retries + 1), 300)
                            }
                        }
                        attemptScroll()
                    } else {
                        mainViewRef.current?.scrollToIndex?.({ index: postIndex, animated: true })
                    }
                } else {
                    warn('PostsList', 'Post not found even after wall refresh', { postId })
                }
            }, 1000) // Dar tiempo para que se actualice la lista
        } catch (e) {
            error('PostsList', 'scrollToPost error', e)
            // Liberar el flag si hay error
            isLoadingProgressivelyRef.current = false
            lastProgressiveLoadPostIdRef.current = null
        }
    }

    const getTerms = async () => {
        try {
            // const now = Date.now()
            // if (getTermsInFlightRef.current || now - lastGetTermsTsRef.current < 800) {
            //     debug('TTCC', 'getTerms throttled/ignored')
            //     return
            // }
            // getTermsInFlightRef.current = true
            // lastGetTermsTsRef.current = now
            let data: any
            if (!isConnected || !hasInternet) {
                // No marcar aceptado en offline; reintentar al reconectar
                return
            }
            if (isConnected && hasInternet) {
                try {
                    info('TTCC', 'get_terms_and_conditions (posts) start')
                } catch {}
                const authToken = await getAuthToken('posts_ttcc')
                if (!authToken) {
                    // Sin token, evitar llamar al endpoint (evita 401 innecesario en primer arranque)
                    debug('TTCC', 'skip get_terms_and_conditions (no authToken)')
                    return
                }
                data = await request_get_terms_and_conditions({ authToken })
                const respVersion = String(data?.single?.version ?? '')
                const hasTerms = !!data?.single?.terms?.message
                try {
                    debug('TTCC', 'get_terms_and_conditions (posts) resp', { status: data?.status, version: respVersion, hasTerms })
                } catch {}
                if (data?.status == 1 || hasTerms) {
                    // Gate by local cache (global terms without course/version)
                    const globalKey = `${ASYNC_STORAGE_KEYS.TERMS_AND_CONDITIONS_ACCEPTED}:global:${respVersion}`
                    try {
                        const accepted = await StorageAdapter.getItem(globalKey)
                        if (accepted === 'true') {
                            try {
                                info('TTCC', 'already accepted (global) skip modal', { version: respVersion })
                            } catch {}
                            console.log('PASO A ACEPTAR TERMS 1 =======>')
                            setTermsAccepted(true)
                            acceptTermsAndConditions()
                        } else {
                            try {
                                info('TTCC', 'show modal (posts) not accepted', { version: respVersion })
                            } catch {}
                            await handle_terms(data?.single?.terms?.message, respVersion)
                        }
                    } catch {
                        try {
                            warn('TTCC', 'cache read error (posts), showing modal')
                        } catch {}
                        await handle_terms(data?.single?.terms?.message, respVersion)
                    }
                    // Guardar último TTCC global
                    try {
                        await StorageAdapter.setItem(
                            ASYNC_STORAGE_KEYS.TERMS_AND_CONDITIONS_LAST_GLOBAL,
                            JSON.stringify({ version: respVersion, terms: String(data?.single?.terms?.message ?? '') }),
                        )
                    } catch {}
                } else {
                    // No pending TTCC: habilitar carga de posts
                    try {
                        info('TTCC', 'no pending TTCC (posts) -> marking accepted to allow posts load')
                    } catch {}
                    console.log('PASO A ACEPTAR TERMS 2 =======>')
                    setTermsAccepted(true)
                    acceptTermsAndConditions()
                }
            }
        } catch (error) {
            error('TTCC', 'getTerms error (posts)', error)
            // No asumir aceptado en error, se reintentará
        } finally {
            getTermsInFlightRef.current = false
        }
    }

    const acceptTerms = async (version?: string) => {
        try {
            let user_id: string | null = null
            try {
                const user = await PostsServices.getUser()
                user_id = user && (user as any)['_id'] ? String((user as any)['_id']) : null
            } catch {}
            if (user_id) {
                try {
                    TrackingServices.addTracking(user_id, 'accept_terms_and_conditions', 'terms_and_conditions_acceptances')
                } catch {}
            }
            let data: any
            let fetch_error = false
            if (isConnected && hasInternet) {
                const authToken = await getAuthToken('posts_ttcc_accept')
                try {
                    debug('TTCC', 'accept_terms_and_conditions (posts) request', {
                        headers: { 'Content-Type': 'application/json', Authorization: authToken ? 'Bearer ***' : '', version: String(version ?? '') },
                    })
                } catch {}
                const response = await fetch(`${apiUrl}/accept_terms_and_conditions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: (authToken as string) || '', version: String(version ?? '') },
                })
                if (!response.ok) {
                    data = { status: 0 }
                    fetch_error = true
                }
                if (!fetch_error) {
                    data = await response.json()
                    try {
                        debug('TTCC', 'accept_terms_and_conditions (posts) resp', {
                            status: data?.status,
                            accepted: !!data?.single?.terms?.accept,
                            version: String(data?.single?.version ?? version ?? ''),
                        })
                    } catch {}
                    if (data?.single && data.status == 1 && data.single.terms && data.single.terms.accept == true) {
                        setTermsAccepted(true)
                        acceptTermsAndConditions()
                        try {
                            const ver = String(version ?? data?.single?.version ?? '')
                            if (ver) {
                                const globalKey = `${ASYNC_STORAGE_KEYS.TERMS_AND_CONDITIONS_ACCEPTED}:global:${ver}`
                                await StorageAdapter.setItem(globalKey, 'true')
                                try {
                                    info('TTCC', 'accepted (posts)', { version: ver })
                                } catch {}
                            }
                        } catch {}
                    }
                }
            }
        } catch (err) {
            error('TTCC', 'acceptTerms error (posts)', err)
        }
    }

    useEffect(() => {
        if (isConnected && hasInternet && webSocketInstance) {
            console.log('[WS][PostsList] subscribing POSTS_VIEWEDS listener (platform:', Platform.OS, ')')

            const handleMessage = async (message: string) => {
                try {
                    console.log('[WS][PostsList] handleMessage RAW (platform:', Platform.OS, '):', message)

                    let data: any
                    try {
                        data = typeof message === 'string' ? JSON.parse(message) : message
                    } catch (e) {
                        console.error('[WS][PostsList] JSON.parse error:', e, 'raw=', message)
                        return
                    }

                    if (data.type === WEBSOCKET_TYPES.POSTS_VIEWEDS) {
                        console.log('[WS] 📊 Received views update:', data.posts?.length, 'posts')

                        // Actualizar el store de interacciones con las nuevas vistas
                        const currentInteractions = useInteractionsStore.getState().interactions || {}
                        const updates: Record<string, any> = {}

                        for (const viewData of data.posts || []) {
                            const postId = String(viewData._id || viewData.post_id)
                            const newViews = Number(viewData.views) || 0

                            // Obtener interacción actual o crear una nueva
                            const currentInteraction = currentInteractions[postId] || {
                                post_id: postId,
                                reactions: [],
                                latest_reactions: [],
                                user_reaction: [],
                                comment_count: 0,
                                comment_first: {},
                                views: 0,
                            }

                            // Solo actualizar si las vistas cambiaron
                            if (currentInteraction.views !== newViews) {
                                const finalViews = Math.max(currentInteraction.views || 0, newViews)

                                if (finalViews > (currentInteraction.views || 0)) {
                                    updates[postId] = {
                                        ...currentInteraction,
                                        views: finalViews,
                                        post_id: postId,
                                    }

                                    console.log('[WS] 📈 Updating views for post', postId, ':', currentInteraction.views, '->', newViews)
                                }
                            }
                        }

                        // Aplicar todas las actualizaciones al store
                        if (Object.keys(updates).length > 0) {
                            setInteractions(updates as any)
                            console.log('[WS] ✅ Updated', Object.keys(updates).length, 'posts with new views')
                        }

                        // También actualizar el contexto de posts (para persistencia)
                        // Actualizar cada post individualmente con sus nuevas vistas
                        try {
                            for (const viewData of data.posts || []) {
                                const postId = String(viewData._id || viewData.post_id)
                                const newViews = Number(viewData.views) || 0

                                // Verificar si el post existe en postsRef antes de actualizar
                                const postExists = postsRef.current?.some((p) => String((p as any)._id) === postId)

                                if (postExists) {
                                    // ✅ CORRECCIÓN: Pasar post_id y views por separado
                                    updateViewPostsContext(postId, newViews)
                                    console.log('[WS] 📝 Updated post context for', postId, 'with views:', newViews)
                                }
                            }
                        } catch (err) {
                            console.warn('[WS] Error updating posts context:', err)
                        }
                    }
                } catch (err) {
                    console.error('[WS] Error handling message:', err)
                }
            }

            webSocketInstance.addEventListener(WEBSOCKET_TYPES.POSTS_VIEWEDS, handleMessage)

            return () => {
                webSocketInstance.removeEventListener(WEBSOCKET_TYPES.POSTS_VIEWEDS, handleMessage)
            }
        }
    }, [isConnected, hasInternet, webSocketInstance, setInteractions, updateViewPostsContext])

    const sendPostsVieweds = React.useCallback(async () => {
        try {
            if (isSendingViewPosts.current == true) {
                debug('Views', 'sendPostsVieweds: already sending, skipping this call')
                return
            }
            isSendingViewPosts.current = true

            if (isConnected && hasInternet) {
                const authToken = await getAuthToken('posts_send_views')
                let postViewed = await PostsServices.get_post_viewed()
                debug('Views', '[QUEUE] size', Array.isArray(postViewed) ? postViewed.length : 0)
                const lasSendVieweds = lastSendView && lastSendView.length > 0 ? lastSendView.map((post) => post['_id']) : []
                if (lasSendVieweds && lasSendVieweds.length > 0) {
                    postViewed = postViewed && postViewed.length > 0 ? postViewed.filter((post: any) => !lasSendVieweds.includes(post['_id'])) : []
                }
                // Evitar lotes demasiado grandes
                const MAX_BATCH = 50
                if (postViewed && postViewed.length > MAX_BATCH) {
                    postViewed = postViewed.slice(0, MAX_BATCH)
                }
                if (postViewed && postViewed.length > 0) {
                    try {
                        if (!authToken) {
                            warn('Views', 'token null')
                        } else {
                            if (webSocketInstance && webSocketInstance.isConnected) {
                                setlastSendView(postViewed)
                                debug('Views', '[SEND] via WS', { items: postViewed.length, readyState: webSocketInstance.getReadyState?.() })
                                webSocketInstance
                                    .send(postViewed, WEBSOCKET_TYPES.POSTS_VIEWEDS, authToken)
                                    .then(async () => {
                                        debug('Views', '[ACK] deleting from local items', postViewed.length)
                                        for (const post of postViewed) {
                                            try {
                                                await PostsServices.delete_posts_viewed_from_local(post['_id'])
                                            } catch (innerError) {
                                                error('Views', 'Error al eliminar el post localmente', innerError)
                                            }
                                        }
                                    })
                                    .catch((error: any) => {
                                        error('Views', 'Error al enviar los datos', error)
                                    })
                            } else {
                                error('Views', 'WebSocket is not connected', webSocketInstance)
                            }
                        }
                    } catch (error) {
                        error('Views', 'Error en la operación del WebSocket', error)
                    }
                }
            }
        } catch (error) {
            error('Views', 'sendPostsVieweds =>', error)
        } finally {
            isSendingViewPosts.current = false
        }
    }, [isConnected, hasInternet, webSocketInstance, lastSendView])

    // Dispara envío cuando se acumulan visualizaciones
    useEffect(() => {
        sendPostsVieweds()
    }, [postVieweds, sendPostsVieweds])

    // Reintenta al recuperar conectividad
    useEffect(() => {
        if (isConnected && hasInternet) {
            sendPostsVieweds()
        }
    }, [isConnected, hasInternet, sendPostsVieweds])

    useEffect(() => {
        if (post_id && dataPosts && (dataPosts as any[]).length > 0) {
            // Esperar un momento para que el DOM se actualice en web
            const timer = setTimeout(
                () => {
                    scrollToPost(post_id)
                },
                Platform.OS === 'web' ? 500 : 100,
            )
            return () => clearTimeout(timer)
        }
    }, [post_id, dataPosts])

    // Diagnóstico adicional: estadística de labels presentes en posts cargados
    // (moved lower after updateFlag declaration)

    const handleCompleteSurvey = (id: string | number) => {
        update_complete_local_posts(id as any, true)
        const updatedPosts = posts.map((post) => (post._id === id ? { ...post, complete: true } : post))
        setPosts(updatedPosts)
    }

    const lastVideoCandidateTsRef = useRef(0)
    const lastVideoCandidateIdRef = useRef<string | number | null>(null)

    const onItemViewedChange = (data: OnViewableItemsChanged<PostItem>) => {
        const now = Date.now()
        // Throttle para no procesar demasiados eventos
        if (now - lastViewChangeTsRef.current < 100) return
        lastViewChangeTsRef.current = now

        const visibleItemsRaw = data?.viewableItems?.filter((i) => i.isViewable) || []
        const visibleItems = [...visibleItemsRaw].sort((a, b) => a.index - b.index)

        // Tracking de viewedItemIndex (para métricas de visualización)
        if (visibleItems.length > 0) {
            const top = visibleItems[0]
            if (top.index !== viewedItemIndexRef.current) {
                const now2 = Date.now()
                const cur = focusCandidateRef.current
                if (!cur || cur.index !== top.index) {
                    focusCandidateRef.current = { index: top.index, count: 1, firstTs: now2 }
                } else {
                    cur.count += 1
                }
                const cand = focusCandidateRef.current
                // Criterios de promoción a foco estable:
                // 1. permanencia temporal >= 140ms
                // 2. al menos 2 eventos consecutivos (count>=2) y tiempo >=110ms desde el primero
                // 3. fallback: si han pasado >500ms desde último commit, permitir con 1 evento tras >=160ms
                const timeSinceFirst = now2 - cand.firstTs
                const sinceLastCommit = now2 - lastCommittedViewedTsRef.current
                const promote =
                    (timeSinceFirst >= 140 && cand.count >= 2) ||
                    (timeSinceFirst >= 110 && cand.count >= 3) ||
                    (sinceLastCommit >= 500 && timeSinceFirst >= 160)
                if (promote) {
                    viewedItemIndexRef.current = cand.index
                    lastCommittedViewedTsRef.current = now2
                    pendingViewedIndexRef.current = null
                    focusCandidateRef.current = null
                    setViewedItemIndex(cand.index)

                    // DEBUG: Log cambio de focus
                    console.log('[PostsList] 🎯 Focus changed to index:', cand.index, 'time:', timeSinceFirst + 'ms', 'count:', cand.count)
                }
            } else {
                // Si el top real coincide con el foco actual, limpiar candidato residual
                focusCandidateRef.current = null
            }
            const topPostId = top?.item?._id
            if (topPostId != null) {
                currentTopVisibleIdRef.current = topPostId
                if (!viewTimersRef.current.has(topPostId)) {
                    const timer = setTimeout(async () => {
                        if (currentTopVisibleIdRef.current !== topPostId) {
                            viewTimersRef.current.delete(topPostId)
                            return
                        }
                        try {
                            if (viewedSentSetRef.current.has(topPostId)) {
                                debug('Views', 'addViewedPost skipped (duplicate)', topPostId)
                            } else {
                                viewedSentSetRef.current.add(topPostId)
                                debug('Views', 'addViewedPost', topPostId)
                                await PostsServices.addViewedPost(topPostId)
                                setPostVieweds((prev) => prev + 1)
                            }
                        } catch (e: any) {
                            warn('Views', 'addViewedPost error', e?.message)
                        } finally {
                            viewTimersRef.current.delete(topPostId)
                        }
                    }, 3000)
                    viewTimersRef.current.set(topPostId, timer)
                }
                for (const [pid, t] of viewTimersRef.current.entries()) {
                    if (pid !== topPostId) {
                        clearTimeout(t)
                        viewTimersRef.current.delete(pid)
                    }
                }
            }
        }
    }

    const [updateFlag, setUpdateFlag] = useState(false)
    const lastPrefetchedVersionRef = useRef<any>(null)

    // Diagnóstico de distribución de labels en posts cargados
    useEffect(() => {
        try {
            const list = postsRef.current || []
            if (!list.length) return
            let withLabels = 0
            const labelFreq: Record<string, number> = {}
            list.forEach((p) => {
                const labelsArr: any[] = Array.isArray((p as any).labels) ? (p as any).labels : []
                if (labelsArr.length) {
                    withLabels++
                    labelsArr.forEach((l) => {
                        const name = l?.name || l?.label || l?.title || l?._id || '??'
                        labelFreq[name] = (labelFreq[name] || 0) + 1
                    })
                }
            })
            const total = list.length
            const distinct = Object.keys(labelFreq).length
            const top = Object.entries(labelFreq)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
            debug('Labels', `[LabelDiag] posts=${total} withLabels=${withLabels} distinctLabels=${distinct} top10=`, top)
        } catch (e) {
            warn('Labels', 'error computing stats', (e as any)?.message)
        }
    }, [updateFlag])

    const prefetchPostImages = useCallback(async (posts: PostItem[]) => {
        try {
            if (isWeb()) return
            const urlsSet = new Set<string>()
            for (const p of posts || []) {
                const medias: any[] = (p as any)?.medias || []
                for (const m of medias) {
                    const type = (m?.media_type || m?.content_type || '').toString().toLowerCase()
                    const url = m?.media_url || m?.media
                    if (type === 'image' && typeof url === 'string' && /^https?:\/\//i.test(url)) {
                        urlsSet.add(url)
                    }
                }
            }
            const allUrls = Array.from(urlsSet)
            if (allUrls.length === 0) return
            const MAX_PREFETCH = 80
            const slice = allUrls.slice(0, MAX_PREFETCH)
            // Prefetch (descarga controlada). Luego, limpiar huérfanos usando el set completo referenciado
            await downloadContent('post', slice, 8)
            await pruneDownloadedContent('post', allUrls)
        } catch (e: any) {
            warn('PostsList', 'prefetchPostImages error:', e?.message)
        }
    }, [])

    const updateInteractions = (newInt: any) => {
        debug('PostsList', 'updateInteractions START', { postId: newInt.post_id })
        // lazy('PostsList', 'debug', () => ['newInt received', JSON.stringify(newInt, null, 2)])

        try {
            // Comparar contra estado actual para evitar updates innecesarios
            const key = String(newInt.post_id)
            const current = useInteractionsStore.getState().interactions?.[key]
            // lazy('PostsList', 'debug', () => ['Current interaction', JSON.stringify(current, null, 2)])
            const currentStr = JSON.stringify(current)
            const newStr = JSON.stringify({ ...newInt, post_id: key })
            if (currentStr === newStr) {
                debug('PostsList', 'Interaction unchanged, skipping update')
                debug('PostsList', 'updateInteractions END (no change)')
                return
            }
            // info('PostsList', 'Interaction changed, updating')
            // debug('PostsList', 'Old user_reaction', current?.user_reaction)
            // debug('PostsList', 'New user_reaction', newInt.user_reaction)
            // Actualizar sólo el post afectado (Zustand mergeará internamente)
            setInteractions({ [key]: { ...newInt, post_id: key } } as any)
            // debug('PostsList', 'updateInteractions END (updated)')
        } catch (e) {
            warn('PostsList', 'updateInteractions failed, falling back to setInteractions direct', (e as any)?.message)
            const key = String(newInt.post_id)
            setInteractions({ [key]: { ...newInt, post_id: key } } as any)
        }
    }

    const deriveLocalInteraction = (post: any) => {
        let rc: any = (post as any)?.reaction_counter
        if (typeof rc === 'string') {
            try {
                rc = JSON.parse(rc)
            } catch {
                rc = []
            }
        }
        if (!Array.isArray(rc)) rc = []
        const viewsLocal = Number(post?.views) || 0
        const commentLocal = Number(post?.comment_count) || 0
        return {
            post_id: String(post?._id),
            reactions: rc,
            latest_reactions: [],
            user_reaction: [],
            comment_count: commentLocal,
            comment_first: post?.comment_first ?? {},
            views: viewsLocal,
        }
    }

    // ---------------- Masonry Experimental (columnas incrementales) ----------------
    // Re-activado en web cuando hay más de una columna y el flag experimental está habilitado
    const isMasonryActive = enableMasonryExperimental && Platform.OS === 'web' && dynamicColumns > 1
    const isCssColumnsMasonryActive = Platform.OS === 'web' && useCssColumnsMasonry && dynamicColumns > 1
    // Paginado manual habilitado: desactiva triggers automáticos (scroll/observer) y muestra botón de "Cargar más"
    const manualPaging = true
    // Para evitar "pantallasos" por montajes/desmontajes rápidos entre FlashList y Masonry
    // hacemos el UI stick una vez que activó y hay datos; no vuelve a FlatList en esta sesión.
    const [isMasonryUI, setIsMasonryUI] = useState(false)
    const [masonryCols, setMasonryCols] = useState<number | null>(null)
    useEffect(() => {
        if (!isMasonryUI && isMasonryActive && Array.isArray(dataPosts) && (dataPosts as any[]).length > 0) {
            // Pequeño delay para estabilizar ancho/columnas
            const t = setTimeout(() => {
                setIsMasonryUI(true)
                setMasonryCols((prev) => prev ?? Math.max(1, dynamicColumns))
            }, 120)
            return () => clearTimeout(t)
        }
        // Nota: no desactivamos isMasonryUI automáticamente para evitar flicker al cambiar columnas
    }, [isMasonryActive, isMasonryUI, dataPosts])

    const effectiveCols = useMemo(
        () => (isMasonryUI ? Math.max(1, masonryCols ?? dynamicColumns) : dynamicColumns),
        [isMasonryUI, masonryCols, dynamicColumns],
    )
    React.useEffect(() => {
        info('PostsList', '🏗️ Masonry status', {
            isMasonryActive,
            enableMasonryExperimental,
            platform: Platform.OS,
            dynamicColumns,
            width,
        })
    }, [isMasonryActive, enableMasonryExperimental, dynamicColumns, width])
    const horizontalGutter = 8
    const verticalGutter = 12
    const [heightMap, setHeightMap] = useState<Record<string, number>>({})
    const heightMapRef = useRef<Record<string, number>>({})
    const sealedHeightsRef = useRef<Set<string | number>>(new Set())
    interface MasonryAssign {
        postId: string
        col: number
        originalIndex: number
        est: number
    }
    const [masonryAssignments, setMasonryAssignments] = useState<MasonryAssign[]>([])
    const colHeightsRef = useRef<number[]>([])
    const assignedSetRef = useRef<Set<string>>(new Set())
    // Guardar snapshot para evitar recalcular columnas completas y provocar parpadeos
    // stableModeRef removido (no se re-balancea)

    // Heurística: intentar derivar ratio desde la URL cuando no hay metadata (e.g., ...1080x1920... ó w=1080&h=1920)
    const parseRatioFromUrl = (url?: string | null): number | null => {
        try {
            if (!url || typeof url !== 'string') return null
            // 1) patrones 1080x1920 o 1080X1920
            let m = url.match(/(\d{3,5})[xX](\d{3,5})/)
            if (m && m[1] && m[2]) {
                const w = Number(m[1])
                const h = Number(m[2])
                if (w > 0 && h > 0) return w / h
            }
            // 2) query params ?w=1080&h=1920 o ?width=1080&height=1920
            const q = url.split('?')[1]
            if (q) {
                const params = new URLSearchParams(q)
                const w = Number(params.get('w') || params.get('width'))
                const h = Number(params.get('h') || params.get('height'))
                if (w > 0 && h > 0) return w / h
            }
            // 3) guiones 1080-1920
            m = url.match(/(\d{3,5})-(\d{3,5})(?!\d)/)
            if (m && m[1] && m[2]) {
                const w = Number(m[1])
                const h = Number(m[2])
                if (w > 0 && h > 0) return w / h
            }
        } catch {}
        return null
    }

    const estimatePostHeight = useCallback((post: any, colWidth: number) => {
        try {
            const HEADER = 110
            const INTERACTIONS = 72
            const FOOTER_PADDING = 24
            const TEXT_BASE = 12
            const medias = (post?.medias || []) as any[]
            let mediaHeight = 0
            if (medias.length) {
                // Intentar usar altura máxima real cacheada primero
                const real = SizeCache.getPostMediaMaxHeight?.(post?._id)
                if (typeof real === 'number' && real > 0) mediaHeight = real
                else {
                    const m0 = medias[0]
                    const type = (m0?.media_type || m0?.content_type || '').toString().toLowerCase()
                    let aspect = 16 / 9
                    if (m0?.aspect_ratio && typeof m0.aspect_ratio === 'number' && m0.aspect_ratio > 0.25 && m0.aspect_ratio < 5)
                        aspect = m0.aspect_ratio
                    else if (m0?.width && m0?.height) aspect = m0.width / m0.height
                    else if (
                        (m0?.image_width || m0?.poster_width || m0?.thumbnail_width || m0?.thumb_width) &&
                        (m0?.image_height || m0?.poster_height || m0?.thumbnail_height || m0?.thumb_height)
                    ) {
                        const iw = Number(m0?.image_width || m0?.poster_width || m0?.thumbnail_width || m0?.thumb_width)
                        const ih = Number(m0?.image_height || m0?.poster_height || m0?.thumbnail_height || m0?.thumb_height)
                        if (iw > 0 && ih > 0) aspect = iw / ih
                    } else {
                        const url = m0?.media_url || m0?.media || ''
                        const parsed = parseRatioFromUrl(url)
                        if (parsed && parsed > 0.25 && parsed < 5) aspect = parsed
                    }
                    if (!(aspect > 0)) aspect = 16 / 9
                    // No clamp exagerado: sólo limitar extremos
                    aspect = Math.min(Math.max(aspect, 0.3), 3)
                    if (type === 'podcast') mediaHeight = 180
                    else mediaHeight = Math.round(colWidth / aspect)
                    mediaHeight = Math.min(mediaHeight, 1000)
                }
            }
            const textLen = (post?.description?.length || 0) + (post?.title?.length || 0)
            const extraText = textLen === 0 ? TEXT_BASE : textLen > 320 ? 110 : textLen > 180 ? 72 : 42
            const total = HEADER + mediaHeight + INTERACTIONS + FOOTER_PADDING + extraText
            return Math.round(total)
        } catch {
            return 420
        }
    }, [])

    const precalcAndCacheHeights = useCallback(
        (posts: any[], availableWidth: number) => {
            try {
                // Determinar ancho de columna real según layout actual
                const totalWidth = width - horizontalGutter * (Math.max(1, effectiveCols) - 1) - 16
                const colWidth = isMasonryUI ? totalWidth / Math.max(1, effectiveCols) : availableWidth

                posts.forEach((p) => {
                    const id = (p as any)?._id
                    if (id == null) return

                    // 1) Precalcular y persistir altura máxima de media por post (basada en ratio/meta/cache)
                    try {
                        const medias = Array.isArray((p as any)?.medias) ? (p as any).medias : []
                        let maxH = 0
                        for (let i = 0; i < medias.length; i++) {
                            const m = medias[i]
                            const type = String(m?.media_type || m?.content_type || '').toLowerCase()
                            if (type === 'podcast') {
                                maxH = Math.max(maxH, 180)
                                continue
                            }
                            const url = m?.media_url || m?.media || ''
                            let aspect: number | null = null
                            if (typeof m?.aspect_ratio === 'number' && m.aspect_ratio > 0.25 && m.aspect_ratio < 5) aspect = m.aspect_ratio
                            else if (m?.width && m?.height && m.height > 0) aspect = m.width / m.height
                            else if (
                                (m?.image_width || m?.poster_width || m?.thumbnail_width || m?.thumb_width) &&
                                (m?.image_height || m?.poster_height || m?.thumbnail_height || m?.thumb_height)
                            ) {
                                const iw = Number(m?.image_width || m?.poster_width || m?.thumbnail_width || m?.thumb_width)
                                const ih = Number(m?.image_height || m?.poster_height || m?.thumbnail_height || m?.thumb_height)
                                if (iw > 0 && ih > 0) aspect = iw / ih
                            } else {
                                const parsed = parseRatioFromUrl(url)
                                if (parsed && parsed > 0.25 && parsed < 5) aspect = parsed
                            }
                            if (!(aspect && aspect > 0.25 && aspect < 5)) {
                                const persisted = url ? SizeCache.getRatioForUrl?.(url) : null
                                if (typeof persisted === 'number' && persisted > 0.25 && persisted < 5) aspect = persisted
                            }
                            // Persistir ratio si pudimos derivarlo de metadata para que el Video lo use al montar
                            try {
                                if (url && aspect && aspect > 0.25 && aspect < 5) {
                                    SizeCache.setRatioForUrl?.(url, aspect)
                                }
                            } catch {}
                            // Fallback razonable y clamp para evitar extremos
                            aspect = aspect && aspect > 0 ? Math.min(Math.max(aspect, 0.3), 3) : 16 / 9
                            const h = Math.round(colWidth / aspect)
                            maxH = Math.max(maxH, Math.min(h, 1000))
                        }
                        if (maxH > 0) {
                            try {
                                SizeCache.setPostMediaMaxHeight?.(id, maxH)
                            } catch {}
                        }
                    } catch {}

                    // 2) Precalcular y persistir altura total de post (estimada) si no existe
                    const existing = SizeCache.getPostHeight(id)
                    if (existing == null) {
                        const est = estimatePostHeight(p, colWidth)
                        SizeCache.setPostHeight(id, est)
                    }
                })
            } catch {}
        },
        [estimatePostHeight, width, dynamicColumns, isMasonryActive],
    )

    // Hash simple para jitter y desempates
    // hashId removido (no se hace jitter)

    // Reset al cambiar cantidad de columnas o desactivar
    useEffect(() => {
        if (!isMasonryActive) {
            if (masonryAssignments.length) {
                setMasonryAssignments([])
                colHeightsRef.current = []
                assignedSetRef.current = new Set()
            }
            return
        }
        if (colHeightsRef.current.length !== effectiveCols) {
            // Reinicio suave sólo si aún no hay asignaciones; si ya existen, mantenemos para evitar flash
            if (masonryAssignments.length === 0) {
                colHeightsRef.current = Array.from({ length: effectiveCols }).map(() => 0)
                setMasonryAssignments([])
                assignedSetRef.current = new Set()
            }
        }
    }, [isMasonryActive, effectiveCols, masonryAssignments.length])

    // Ref para evitar loops infinitos en el efecto de masonry
    const masonryUpdateInProgressRef = useRef(false)
    const lastMasonryUpdateTsRef = useRef(0)

    // Asignación incremental basada en menor altura acumulada
    useEffect(() => {
        if (!isMasonryUI) return
        const list = (dataPosts as PostItem[]) || []
        if (!list.length) return

        // Protección contra loops: no ejecutar si ya se está ejecutando o si se ejecutó hace menos de 100ms
        const now = Date.now()
        if (masonryUpdateInProgressRef.current || now - lastMasonryUpdateTsRef.current < 100) {
            debug('PostsList', 'Masonry update throttled')
            return
        }

        masonryUpdateInProgressRef.current = true
        lastMasonryUpdateTsRef.current = now

        try {
            const totalWidth = width - horizontalGutter * (effectiveCols - 1) - 16
            const colWidth = totalWidth / effectiveCols
            const newAssignments: MasonryAssign[] = []
            for (let i = 0; i < list.length; i++) {
                const p: any = list[i]
                if (assignedSetRef.current.has(String(p._id))) continue
                if (colHeightsRef.current.length !== effectiveCols) {
                    colHeightsRef.current = Array.from({ length: effectiveCols }).map(() => 0)
                }
                // Selección columna con menor altura (sin jitter, estable)
                let target = 0
                for (let c = 1; c < effectiveCols; c++) if (colHeightsRef.current[c] < colHeightsRef.current[target]) target = c
                const est = heightMap[p._id] || estimatePostHeight(p, colWidth)
                colHeightsRef.current[target] += est + verticalGutter
                newAssignments.push({ postId: p._id, col: target, originalIndex: i, est })
                assignedSetRef.current.add(String(p._id))
            }
            if (newAssignments.length) setMasonryAssignments((prev) => [...prev, ...newAssignments])
        } finally {
            masonryUpdateInProgressRef.current = false
        }
    }, [isMasonryUI, dataPosts, effectiveCols, width, heightMap, estimatePostHeight])

    const onMasonryItemLayout = useCallback(
        (postId: string, h: number) => {
            if (!isMasonryActive) return
            if (!postId || !h) return
            // Sólo actualizar si no existe altura previa para evitar cascada de re-renders
            setHeightMap((prev) => {
                if (prev[postId]) return prev
                const next = { ...prev, [postId]: h }
                heightMapRef.current = next
                try {
                    ;(PostsServices as any).savePostHeight?.(postId, h)
                } catch {}
                return next
            })
        },
        [isMasonryActive],
    )

    // Eliminado rebalance para evitar flashes; modo estable incremental

    const masonryColumns = useMemo(() => {
        if (!isMasonryUI) return []
        const list = (dataPosts as PostItem[]) || []
        const cols: { post: PostItem; originalIndex: number }[][] = Array.from({ length: effectiveCols }).map(() => [])
        for (const a of masonryAssignments) {
            const post = list[a.originalIndex]
            // Validar que el índice de columna esté dentro del rango
            if (post && a.col >= 0 && a.col < effectiveCols) {
                cols[a.col].push({ post, originalIndex: a.originalIndex })
            }
        }
        return cols
    }, [isMasonryUI, masonryAssignments, dataPosts, effectiveCols])

    // Precarga de alturas persistidas (solo una vez)
    const heightsLoadedRef = useRef(false)
    useEffect(() => {
        if (heightsLoadedRef.current) return
        heightsLoadedRef.current = true
        ;(async () => {
            try {
                const stored = await (PostsServices as any).getAllHeights?.()
                if (stored && Object.keys(stored).length > 0) {
                    setHeightMap((prev) => ({ ...stored, ...prev }))
                    heightMapRef.current = { ...stored, ...heightMapRef.current }
                    debug('PostsList', 'preload post_height count ->', Object.keys(stored).length)
                }
            } catch (e) {
                warn('PostsList', 'load post_height cache error', (e as any)?.message)
            }
        })()
    }, [])

    const assignmentMap = useMemo(() => {
        const m = new Map<string | number, MasonryAssign>()
        for (const a of masonryAssignments) m.set(a.postId, a)
        return m
    }, [masonryAssignments])

    const MasonryColumnCard: React.FC<{ post: PostItem; originalIndex: number; colIdx: number }> = ({ post, originalIndex }) => {
        const anim = useRef(new Animated.Value(0)).current
        const added = newlyAddedIds.has((post as any)._id)
        const overlayAnim = useRef(new Animated.Value(added ? 1 : 0)).current
        const assign = assignmentMap.get((post as any)._id)
        const estimatedHeight = assign?.est
        const hasAnimated = useRef(false)

        useEffect(() => {
            // Solo animar si NO ha sido animado antes
            if (!hasAnimated.current) {
                Animated.timing(anim, { toValue: 1, duration: 260, delay: Math.min(originalIndex, 40) * 12, useNativeDriver: true }).start()
                hasAnimated.current = true
            }

            if (added) {
                Animated.timing(overlayAnim, { toValue: 0, duration: 420, delay: 90, useNativeDriver: true }).start(() => {
                    setNewlyAddedIds((prev) => {
                        const n = new Set(prev)
                        n.delete((post as any)._id)
                        return n
                    })
                })
            }
        }, [added]) // Solo depender de 'added', no de 'anim' ni 'originalIndex'
        const measured = !!heightMap[(post as any)._id]
        return (
            <Animated.View
                // @ts-ignore - data-post-id es un atributo HTML personalizado para web
                {...(Platform.OS === 'web' ? { 'data-post-id': (post as any)._id } : {})}
                onLayout={(e) => {
                    if (!measured) onMasonryItemLayout((post as any)._id, e.nativeEvent.layout.height)
                }}
                style={{
                    marginBottom: verticalGutter,
                    opacity: anim,
                    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) }],
                    position: 'relative',
                    // Estabilizar altura inicial con estimación para reducir salto
                    minHeight: measured ? undefined : estimatedHeight ? Math.max(estimatedHeight * 0.85, Math.min(estimatedHeight, 900)) : undefined,
                }}
            >
                {renderItem({ item: post, index: originalIndex } as any)}
                {added && (
                    <Animated.View
                        pointerEvents='none'
                        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.15)', opacity: overlayAnim, borderRadius: 12 }}
                    />
                )}
            </Animated.View>
        )
    }

    // --- Scroll infinito para masonry ---
    const masonryScrollRef = useRef<any>(null)
    const masonryPagingLockRef = useRef(false)
    const [isAppending, setIsAppending] = useState(false)
    const autoLoadCountRef = useRef(0) // Contador para limitar auto-cargas
    const hasCheckedInitialLoadRef = useRef(false) // Persistir entre re-renders

    const triggerLoadMore = useCallback(async () => {
        debug('PostsList', 'triggerLoadMore called', {
            isAppending,
            isFetching: isFetchingMoreRef.current,
            masonryLock: masonryPagingLockRef.current,
        })
        if (isAppending || isFetchingMoreRef.current) {
            debug('PostsList', 'triggerLoadMore blocked - isAppending or isFetching')
            return
        }
        masonryPagingLockRef.current = true
        setIsAppending(true)
        try {
            await handleEndReached()
        } finally {
            setTimeout(() => {
                setIsAppending(false)
                masonryPagingLockRef.current = false
                debug('PostsList', 'triggerLoadMore completed, locks released')
            }, 120) // pequeño buffer para montar items sin parpadeo
        }
    }, [handleEndReached]) // REMOVIDO isAppending para evitar re-creaciones

    // Efecto separado para re-verificar después de cargar posts - DESHABILITADO temporalmente
    // React.useEffect(() => {
    //   if (!isMasonryActive || Platform.OS !== 'web') return
    //   if (isAppending || isFetchingMoreRef.current) return
    //   if ((dataPosts as any[])?.length === 0) return
    //
    //   const scrollElement = masonryScrollRef.current
    //   if (!scrollElement) return
    //
    //   const findScrollableElement = (el: any, depth = 0): any => {
    //     if (!el || depth > 3) return null
    //     const style = window.getComputedStyle(el)
    //     const hasOverflow = style.overflowY === 'scroll' || style.overflowY === 'auto'
    //     if (hasOverflow) return el
    //     if (depth < 3) {
    //       for (let child of el.children) {
    //         const found = findScrollableElement(child, depth + 1)
    //         if (found) return found
    //       }
    //     }
    //     return null
    //   }
    //
    //   const actualScrollElement = findScrollableElement(scrollElement) || scrollElement
    //
    //   // Verificar si todavía no hay scroll después de cargar
    //   const recheckAfterLoad = () => {
    //     const scrollHeight = actualScrollElement.scrollHeight
    //     const clientHeight = actualScrollElement.clientHeight
    //
    //     if (!scrollHeight || !clientHeight) return
    //
    //     const needsScroll = scrollHeight <= clientHeight + 50
    //
    //     if (needsScroll && autoLoadCountRef.current === 0) {
    //       console.log('[PostsList] 🔄 Still no scroll after load, loading more', {
    //         scrollHeight,
    //         clientHeight,
    //         posts: (dataPosts as any[])?.length
    //       })
    //       autoLoadCountRef.current = 1
    //       triggerLoadMore()
    //     } else if (!needsScroll && autoLoadCountRef.current > 0) {
    //       console.log('[PostsList] ✅ Scroll now available!', { scrollHeight, clientHeight })
    //       autoLoadCountRef.current = 0
    //     }
    //   }
    //
    //   const timer = setTimeout(() => {
    //     requestAnimationFrame(recheckAfterLoad)
    //   }, 300)
    //
    //   return () => clearTimeout(timer)
    // }, [(dataPosts as any[])?.length, isMasonryActive, isAppending])

    // Listener de scroll nativo para web - DESHABILITADO para paginado manual
    // Listener de scroll nativo para web - DESHABILITADO para paginado manual
    // React.useEffect(() => {
    //   if (!isMasonryActive || Platform.OS !== 'web') return
    //
    //   const scrollElement = masonryScrollRef.current
    //   if (!scrollElement) return
    //
    //   // Buscar el elemento con overflow-y configurado
    //   const findScrollableElement = (el: any, depth = 0): any => {
    //     if (!el || depth > 3) return null
    //
    //     const style = window.getComputedStyle(el)
    //     const hasOverflow = style.overflowY === 'scroll' || style.overflowY === 'auto'
    //
    //     if (hasOverflow) return el
    //
    //     if (depth < 3) {
    //       for (let child of el.children) {
    //         const found = findScrollableElement(child, depth + 1)
    //         if (found) return found
    //       }
    //     }
    //
    //     return null
    //   }
    //
    //   const actualScrollElement = findScrollableElement(scrollElement) || scrollElement
    //   console.log('[PostsList] 📜 scroll listener attached to:', actualScrollElement.tagName)
    //
    //   let lastLogTime = 0
    //   let lastScrollTop = 0
    //
    //   const handleScroll = () => {
    //     const scrollTop = actualScrollElement.scrollTop
    //     const scrollHeight = actualScrollElement.scrollHeight
    //     const clientHeight = actualScrollElement.clientHeight
    //     const distanceToBottom = scrollHeight - (scrollTop + clientHeight)
    //
    //     // Si detectamos scroll real del usuario, resetear el contador de auto-carga
    //     if (Math.abs(scrollTop - lastScrollTop) > 10) {
    //       if (autoLoadCountRef.current > 0) {
    //         console.log('[PostsList] 🔓 User scrolled, resetting auto-load counter')
    //         autoLoadCountRef.current = 0
    //       }
    //       lastScrollTop = scrollTop
    //     }
    //
    //     // Threshold más pequeño y progresivo
    //     const threshold = Math.min(800, clientHeight * 0.5)
    //
    //     // Log throttle - solo logear cada 500ms para no saturar consola
    //     const now = Date.now()
    //     if (now - lastLogTime > 500) {
    //       console.log('[PostsList] scroll event:', {
    //         scrollTop: Math.round(scrollTop),
    //         scrollHeight,
    //         clientHeight,
    //         distanceToBottom: Math.round(distanceToBottom),
    //         threshold: Math.round(threshold),
    //         shouldLoad: distanceToBottom < threshold
    //       })
    //       lastLogTime = now
    //     }
    //
    //     if (distanceToBottom < threshold && !isFetchingMoreRef.current && !masonryPagingLockRef.current) {
    //       console.log('[PostsList] 🔄 loading more posts (scroll detected)')
    //       triggerLoadMore()
    //     }
    //   }
    //
    //   actualScrollElement.addEventListener('scroll', handleScroll, { passive: true })
    //
    //   return () => {
    //     console.log('[PostsList] 🔌 scroll listener removed')
    //     actualScrollElement.removeEventListener('scroll', handleScroll)
    //   }
    // }, [isMasonryActive, triggerLoadMore])

    const onMasonryScroll = useCallback(
        (e: any) => {
            if (manualPaging) return
            if (!isMasonryUI) return
            const { contentOffset, contentSize, layoutMeasurement } = e?.nativeEvent || {}
            if (!contentOffset || !contentSize || !layoutMeasurement) return
            const y = contentOffset.y
            const visibleH = layoutMeasurement.height
            const totalH = contentSize.height
            const distanceToBottom = totalH - (y + visibleH)
            const dynamicThreshold = Math.min(1200, visibleH * 1.4)

            lazy('PostsList', 'debug', () => [
                'onMasonryScroll',
                {
                    y,
                    visibleH,
                    totalH,
                    distanceToBottom,
                    dynamicThreshold,
                    shouldTrigger: distanceToBottom < dynamicThreshold,
                    isFetching: isFetchingMoreRef.current,
                    masonryLock: masonryPagingLockRef.current,
                },
            ])
            if (distanceToBottom < dynamicThreshold && !isFetchingMoreRef.current && !masonryPagingLockRef.current) {
                debug('PostsList', 'triggering load more from scroll')
                triggerLoadMore()
            }
        },
        [isMasonryUI, triggerLoadMore],
    )

    // --- Infinite scroll para CSS Columns (web) usando IntersectionObserver ---
    const cssColumnsContainerRef = React.useRef<any>(null)
    const cssColumnsSentinelRef = React.useRef<any>(null)

    React.useEffect(() => {
        if (manualPaging) return
        if (!isCssColumnsMasonryActive || Platform.OS !== 'web') return
        const sentinel = cssColumnsSentinelRef.current
        if (!sentinel) return

        let cancelled = false
        let cleanupScroll: (() => void) | undefined

        const tryTrigger = () => {
            if (!isFetchingMoreRef.current && !masonryPagingLockRef.current) {
                triggerLoadMore()
            }
        }

        const setupObserver = () => {
            try {
                const IO: any = (window as any).IntersectionObserver
                if (typeof IO !== 'function') throw new Error('IO not available')
                const observer = new IO(
                    (entries: any[]) => {
                        if (cancelled) return
                        for (const entry of entries) {
                            if (entry.isIntersecting) {
                                tryTrigger()
                            }
                        }
                    },
                    { root: null, rootMargin: '800px 0px', threshold: 0 },
                )
                observer.observe(sentinel)
                return () => observer.disconnect()
            } catch {
                // Fallback: listener de scroll de ventana
                const onScroll = () => {
                    try {
                        const doc = document.documentElement
                        const scrollY = (window as any).scrollY || (doc && doc.scrollTop) || 0
                        const innerH = (window as any).innerHeight || 0
                        const scrollH = doc ? doc.scrollHeight : 0
                        const distanceToBottom = scrollH - (scrollY + innerH)
                        const threshold = Math.min(1200, Math.max(600, innerH * 0.8))
                        if (distanceToBottom < threshold && !isFetchingMoreRef.current && !masonryPagingLockRef.current) {
                            tryTrigger()
                        }
                    } catch {}
                }
                window.addEventListener('scroll', onScroll, { passive: true } as any)
                cleanupScroll = () => window.removeEventListener('scroll', onScroll as any)
                return cleanupScroll
            }
        }

        const cleanup = setupObserver()
        return () => {
            cancelled = true
            try {
                cleanup && cleanup()
            } catch {}
        }
    }, [isCssColumnsMasonryActive, triggerLoadMore, (dataPosts as any[])?.length])

    // Autocarga si no hay scroll suficiente en CSS Columns (una sola vez hasta que haya scroll)
    React.useEffect(() => {
        if (manualPaging) return
        if (!isCssColumnsMasonryActive || Platform.OS !== 'web') return
        const recheckAfterLoad = () => {
            try {
                const doc = document.documentElement
                const canScroll = doc && doc.scrollHeight > doc.clientHeight + 50
                if (!canScroll && autoLoadCountRef.current === 0 && Array.isArray(dataPosts) && (dataPosts as any[]).length > 0) {
                    autoLoadCountRef.current = 1
                    triggerLoadMore()
                } else if (canScroll && autoLoadCountRef.current > 0) {
                    autoLoadCountRef.current = 0
                }
            } catch {}
        }
        const t = setTimeout(() => requestAnimationFrame(recheckAfterLoad), 300)
        return () => clearTimeout(t)
    }, [(dataPosts as any[])?.length, isCssColumnsMasonryActive, triggerLoadMore])

    interface FadeInPostProps {
        post: PostItem
        index: number
        viewedItemIndex: number
        allReactionsRef: any
        added: boolean
    }

    const FadeInPostInner: React.FC<FadeInPostProps> = ({ post, index, viewedItemIndex, allReactionsRef, added }) => {
        const anim = React.useRef(new Animated.Value(0)).current
        const overlayAnim = React.useRef(new Animated.Value(added ? 1 : 0)).current
        const hasAnimated = React.useRef(false)

        // Animación de entrada: sólo opacidad para no alterar layout y evitar micro desplazamientos
        useEffect(() => {
            if (!hasAnimated.current) {
                const baseDelay = index < 9 ? index * 18 : 0
                Animated.timing(anim, { toValue: 1, duration: 180, delay: baseDelay, useNativeDriver: true }).start()
                hasAnimated.current = true
            }
            if (added) {
                Animated.timing(overlayAnim, { toValue: 0, duration: 420, delay: 90, useNativeDriver: true }).start(() => {
                    setNewlyAddedIds((prev) => {
                        if (!prev.has((post as any)._id)) return prev
                        const next = new Set(prev)
                        next.delete((post as any)._id)
                        return next
                    })
                })
            }
        }, [added, index])

        // Sólo suscribirse a la interacción específica
        const interactionFromStore = useInteractionsStore(
            React.useCallback((state: any) => (state.interactions as any)?.[String((post as any)._id)], [(post as any)._id]),
        )
        const interaction = interactionFromStore || deriveLocalInteraction(post)
        const interactionsReady = !!interaction
        const measured = !!heightMap[(post as any)._id]

        return (
            <Animated.View
                // @ts-ignore - data-post-id es un atributo HTML personalizado para web
                {...(Platform.OS === 'web' ? { 'data-post-id': (post as any)._id } : {})}
                onLayout={(e) => {
                    const pid = (post as any)._id
                    const h = e.nativeEvent.layout.height
                    if (!h || h <= 60) return

                    // Umbral para evitar micro-ajustes por sub-píxel/font metrics
                    const THRESHOLD = 4
                    setHeightMap((prev) => {
                        const prevH = (prev && prev[pid]) || SizeCache.getPostHeight(pid) || 0
                        const deltaRaw = h - prevH
                        if (Math.abs(deltaRaw) < THRESHOLD) {
                            return prev || {}
                        }

                        const next = { ...(prev || {}), [pid]: h }
                        heightMapRef.current = next
                        try {
                            // Persistir nueva altura consolidada para estabilizar mediciones futuras
                            ;(PostsServices as any).savePostHeight?.(pid, h)
                            SizeCache.setPostHeight(pid, h)
                        } catch {}

                        // Compensar el scroll si el item está por encima del top visible (no compensar el item top para evitar sobre-ajuste)
                        try {
                            const topIdx = viewedItemIndexRef.current
                            if (typeof topIdx === 'number' && index < topIdx) {
                                const cur = lastScrollYRef.current || 0
                                const nextOffset = Math.max(0, cur + deltaRaw)
                                if (cur !== nextOffset) {
                                    mainViewRef.current?.scrollToOffset?.({ offset: nextOffset, animated: false })
                                    lastScrollYRef.current = nextOffset
                                    debug('PostsList', 'scroll compensated after height change', { pid, index, delta: deltaRaw })
                                }
                            }
                        } catch {}

                        return next
                    })
                }}
                style={{ opacity: anim, position: 'relative' }}
            >
                <PostsItemDefault
                    {...(post as any)}
                    handleCompleteSurvey={handleCompleteSurvey}
                    reactions={allReactionsRef}
                    key={(post as any)?._id}
                    shouldAutoPlay={false}
                    isVisible={viewedItemIndex === index}
                    interactionPost={interaction}
                    interactionsReady={interactionsReady}
                    updateInteractions={(i: any) => updateInteractions(i)}
                    viewedItemIndex={viewedItemIndex}
                    onFirstMediaReady={handleFirstMediaReady}
                    listIndex={index}
                />
                {added && (
                    <Animated.View
                        pointerEvents='none'
                        style={{ position: 'absolute', inset: 0, backgroundColor: '#fff', opacity: overlayAnim, borderRadius: 12 }}
                    >
                        {React.createElement(require('../customUtils/Components/Shimmer').default, {
                            style: { position: 'absolute', inset: 0, borderRadius: 12 },
                        })}
                    </Animated.View>
                )}
            </Animated.View>
        )
    }

    const FadeInPost = React.memo(FadeInPostInner, (prev, next) => {
        if ((prev.post as any)?._id !== (next.post as any)?._id) return false
        // Visibilidad cambió?
        const wasVisible = prev.viewedItemIndex === prev.index
        const isVisible = next.viewedItemIndex === next.index
        if (wasVisible !== isVisible) return false
        // Catálogo de reacciones cambió de referencia
        if (prev.allReactionsRef !== next.allReactionsRef) return false
        // Estado de añadido (overlay) cambió
        if (prev.added !== next.added) return false
        return true
    })

    const renderItem = useCallback(
        ({ item: post, index }: { item: PostItem; index: number }) => (
            <FadeInPost
                post={post}
                index={index}
                viewedItemIndex={viewedItemIndex}
                allReactionsRef={allReactions}
                added={newlyAddedIds.has((post as any)._id)}
            />
        ),
        [viewedItemIndex, allReactions],
    )

    const openFormModal = (formData: any) => {
        if (formData) {
            openBottomSheet(
                <View style={{ flex: 1, paddingHorizontal: 5 }}>
                    <Image
                        style={[{ alignSelf: 'center', width: 132, height: 132, marginVertical: 8 }]}
                        source={require('../../../assets/form_pending_nickname.png')}
                    />
                    <Text style={{ textAlign: 'center', fontSize: 18, marginVertical: 5, fontWeight: '700', color: 'black', marginHorizontal: 10 }}>
                        {'Todavia no definiste tu alias'}
                    </Text>
                    <Text
                        style={{
                            textAlign: 'center',
                            fontSize: 15,
                            marginBottom: 10,
                            color: theme.colors.darkGray,
                            lineHeight: 20,
                            marginHorizontal: 10,
                        }}
                    >
                        {'Elije dentro de los ALIAS disponibles, una vez que confirmes se validará que aún no haya sido utilizado por nadie'}
                    </Text>

                    <FormView
                        key={(formData as any)?._id}
                        formContent={(formData as any)?.questions}
                        hideOrShow={() => {}}
                        form_id={(formData as any)?._id}
                        store_id={''}
                        schedule_id={''}
                        repeat={(formData as any)?.repeat}
                        progress={(formData as any)?.progress}
                        totalScore={(formData as any)?.totalScore}
                        clearRegisterDatabaseLocal={null as any}
                        hasFinishSheet={false}
                        reloadHideShowData={null as any}
                        tags={[]}
                        onClickTag={() => {}}
                        formType={(formData as any)?.type_form.type}
                        actionButonSend={() => {}}
                        formTypeObj={(formData as any).type_form}
                        deleteForm={(alias: string) => {
                            setForm(null)
                            resetCache()
                            updateUserAlias(alias)
                            setAlreadySentForm(true)
                            openConfirmFormModal(alias)
                        }}
                    />
                </View>,
            )
        }
    }

    async function updateUserAlias(alias: string) {
        try {
            const user = await UserServices.getUser()
            if (!user) {
                error('PostsList', 'User not found')
                return
            }
            const ID = user._id
            const UPDATES = { nickname: alias }
            UserServices.updateUser(ID, UPDATES)
        } catch (error) {
            error('PostsList', 'Error updating user alias:', error)
        }
    }

    const openConfirmFormModal = (alias: string) => {
        openBottomSheet(
            <View style={{ flex: 1 }}>
                <Image
                    style={[{ alignSelf: 'center', width: 140, height: 140, marginVertical: 8 }]}
                    source={require('../../../assets/form_confirm_nickname.png')}
                />
                <Text style={{ textAlign: 'center', fontSize: 18, marginVertical: 5, fontWeight: '700', color: 'black' }}>
                    {'¡Alias confirmado!'}
                </Text>
                <Text style={{ textAlign: 'center', fontSize: 15, marginBottom: 10, color: theme.colors.darkGray, fontWeight: '400' }}>
                    {'Tu alias elegido es: '}
                    <Text style={{ fontWeight: '700' }}>"{alias}"</Text>
                    {'\n¡Ya podés usarlo como tu identificador en la App!'}
                </Text>
                <TouchableOpacity
                    onPress={() => closeBottomSheet(true)}
                    style={{
                        alignSelf: 'center',
                        padding: 10,
                        marginBottom: 25,
                        flex: 1,
                        borderRadius: 10,
                        borderColor: theme.colors.colorSecondary,
                        borderWidth: 1.5,
                        width: '90%',
                        marginHorizontal: 15,
                        alignItems: 'center',
                        marginTop: 10,
                    }}
                >
                    <Text style={{ color: theme.colors.colorSecondary, fontWeight: '700', alignContent: 'center', fontSize: 16 }}>Aceptar</Text>
                </TouchableOpacity>
            </View>,
        )
    }

    useEffect(() => {
        if (isFocused && form != null) {
            openFormModal(form)
        }
    }, [isFocused])

    const SkeletonItem = useCallback(
        () => (
            <View
                style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 12,
                    shadowColor: '#000',
                    shadowOpacity: 0.05,
                    shadowRadius: 4,
                    shadowOffset: { width: 0, height: 2 },
                }}
            >
                {/* Media placeholder */}
                <View style={{ width: '100%', aspectRatio: 16 / 9 }}>
                    {/* @ts-ignore - dynamic import require */}
                    {React.createElement(require('../customUtils/Components/Shimmer').default, {
                        style: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, borderRadius: 8 },
                    })}
                </View>
                <View style={{ height: 10 }} />
                {React.createElement(require('../customUtils/Components/Shimmer').default, { style: { width: '60%', height: 14, borderRadius: 6 } })}
                <View style={{ height: 8 }} />
                {React.createElement(require('../customUtils/Components/Shimmer').default, { style: { width: '40%', height: 12, borderRadius: 6 } })}
            </View>
        ),
        [],
    )

    const SkeletonList = useCallback(() => {
        const count = Platform.OS === 'web' && dynamicColumns > 1 ? 8 : 5
        const items = Array.from({ length: count })

        // Si estamos en web con múltiples columnas, distribuir en grid
        if (Platform.OS === 'web' && dynamicColumns > 1) {
            const columns: any[][] = Array.from({ length: dynamicColumns }).map(() => [])
            items.forEach((_, i) => {
                const colIndex = i % dynamicColumns
                columns[colIndex].push(i)
            })

            return (
                <View
                    style={{
                        flexDirection: 'row',
                        paddingHorizontal: 8,
                        alignItems: 'flex-start',
                    }}
                >
                    {columns.map((columnItems, colIdx) => (
                        <View
                            key={`skeleton-col-${colIdx}`}
                            style={{
                                flex: 1 / dynamicColumns,
                                paddingLeft: colIdx === 0 ? 0 : 8,
                            }}
                        >
                            {columnItems.map((itemIndex) => (
                                <SkeletonItem key={`skeleton-${itemIndex}`} />
                            ))}
                        </View>
                    ))}
                </View>
            )
        }

        // Mobile: una sola columna vertical
        return (
            <View style={{ paddingHorizontal: Platform.OS === 'web' ? 8 : 0 }}>
                {items.map((_, i) => (
                    <SkeletonItem key={`skeleton-${i}`} />
                ))}
            </View>
        )
    }, [dynamicColumns, SkeletonItem])

    // Incluir una "versión" del catálogo de reacciones en extraData para forzar re-render de ítems cuando cambia
    const reactionsKey = useMemo(() => {
        try {
            const arr = Array.isArray(allReactions) ? allReactions : []
            // Usar _id o name como firma estable; evita stringify completo para no ser pesado
            return `${arr.length}|${arr.map((r: any) => r?._id ?? r?.reaction_id ?? r?.name ?? '').join(',')}`
        } catch {
            return '0|'
        }
    }, [allReactions])

    const extraDataMemo = useMemo(() => ({ viewedItemIndex, reactionsKey, resumeTick }), [viewedItemIndex, reactionsKey, resumeTick])
    // ❌ updateFlag removido - causaba re-renders innecesarios de toda la lista

    // Fallback diagnóstico: si en web no hay labels ni posts y no está loading -> mostrar panel de estado
    // ✅ SOLO mostrar si llevamos al menos 2 segundos sin datos (evita flash durante carga inicial)
    const [showDiagnostics, setShowDiagnostics] = React.useState(false)

    React.useEffect(() => {
        const noData = !loading && (!dataPosts || (dataPosts as any[])?.length === 0) && (!postsContext || (postsContext as any[])?.length === 0)

        if (noData) {
            // Esperar 2 segundos antes de mostrar diagnóstico (evita flash)
            const timer = setTimeout(() => setShowDiagnostics(true), 2000)
            return () => clearTimeout(timer)
        } else {
            setShowDiagnostics(false)
        }
    }, [loading, dataPosts, postsContext])

    // En web: si no hay datos, preferimos mostrar skeleton + mensaje inline en lugar de reemplazar toda la vista
    // El panel de diagnóstico se desactiva para no confundir al usuario final

    return (
        <>
            <View style={styles.viewLabels}>
                {labels.length > 0 && (
                    <FlatList
                        data={labels}
                        keyExtractor={(item, index) => `${item?._id ?? 'null'}-${item?.name ?? ''}-${index}`}
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.containerPostFlags}
                        renderItem={({ item: label }: { item: LabelItem }) => (
                            <PostFlags posts={label as any} onSelectLabel={handleSelectLabel} onPressFavourite={onClickFavourite} />
                        )}
                    />
                )}
            </View>
            {!isMobile && <View style={{ height: 8 }} />}
            <View style={[styles.container, { overflow: 'visible' }]}>
                {loading || (typeof labelStore === 'string' && loadingFetchInternal) ? (
                    <SkeletonList />
                ) : (
                    <>
                        {!dataPosts || (dataPosts as PostItem[]).length === 0 ? (
                            <>
                                {selectedLabel === -1 ? (
                                    <View style={styles.containerNoPosts}>
                                        <Image style={styles.image} source={require('../../../assets/noFavPosts.png')} />
                                        <Text style={styles.title}>{languageFile.no_post_saved}</Text>
                                        <Text style={styles.description}>{languageFile.see_your_favorite_content}</Text>
                                    </View>
                                ) : (
                                    <View style={styles.containerNoPosts}>
                                        <Image style={styles.image} source={require('../../../assets/noPosts.png')} />
                                        <Text style={styles.title}>{languageFile.no_publications}</Text>
                                        <Text style={styles.description}>{languageFile.no_post_to_read}</Text>
                                    </View>
                                )}
                            </>
                        ) : isCssColumnsMasonryActive ? (
                            // Alternativa web: columnas flex con distribución por índice (fila-primero)
                            // Pros: respeta orden izquierda→derecha y arriba→abajo según dataPosts (más reciente a más antiguo).
                            // Contras: no balancea alturas entre columnas (no es masonry real), pero evita el orden vertical de CSS columns.
                            <>
                                {(() => {
                                    const colCount = Math.max(1, Math.min(6, dynamicColumns))
                                    const cols: { post: PostItem; originalIndex: number }[][] = Array.from({ length: colCount }).map(() => [])
                                    const list = (dataPosts as PostItem[]) || []
                                    for (let i = 0; i < list.length; i++) {
                                        const colIdx = i % colCount
                                        cols[colIdx].push({ post: list[i], originalIndex: i })
                                    }
                                    return React.createElement(
                                        'div',
                                        {
                                            ref: cssColumnsContainerRef,
                                            className: `masonry-flex cols-${colCount}`,
                                            style: {
                                                padding: 0,
                                                paddingBottom: 12,
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                gap: 12,
                                                // Hint GPU
                                                backfaceVisibility: 'hidden',
                                                transform: 'translateZ(0)',
                                            },
                                            role: 'list',
                                        },
                                        ...cols.map((col, cIdx) =>
                                            React.createElement(
                                                'div',
                                                {
                                                    key: `flex-col-${cIdx}`,
                                                    style: {
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 12,
                                                        minWidth: 0,
                                                    },
                                                },
                                                ...col.map(({ post, originalIndex }) =>
                                                    React.createElement(
                                                        'div',
                                                        {
                                                            key: (post as any)?._id || `${cIdx}-${originalIndex}`,
                                                            'data-post-id': (post as any)?._id,
                                                            role: 'listitem',
                                                            style: {
                                                                // mantén consistencia visual con margen inferior
                                                                marginBottom: 0,
                                                            },
                                                        },
                                                        renderItem({ item: post, index: originalIndex } as any),
                                                    ),
                                                ),
                                            ),
                                        ),
                                    )
                                })()}
                                {!manualPaging &&
                                    React.createElement('div', {
                                        key: 'css-columns-sentinel',
                                        ref: cssColumnsSentinelRef,
                                        style: { width: '100%', height: 1 },
                                        'data-sentinel': 'true',
                                    })}
                                {/* Footer manual para cargar más */}
                                <View style={{ width: '100%', alignItems: 'center', paddingBottom: 24, paddingTop: 8 }}>
                                    {isAppending ? (
                                        <View
                                            style={{
                                                backgroundColor: '#fff',
                                                paddingHorizontal: 24,
                                                paddingVertical: 12,
                                                borderRadius: 24,
                                                shadowColor: '#000',
                                                shadowOffset: { width: 0, height: 2 },
                                                shadowOpacity: 0.1,
                                                shadowRadius: 8,
                                                elevation: 4,
                                            }}
                                        >
                                            {React.createElement(require('../customUtils/Components/Shimmer').default, {
                                                style: { width: 140, height: 18, borderRadius: 12 },
                                            })}
                                            <Text style={{ marginTop: 6, fontSize: 12, color: '#666', textAlign: 'center' }}>Cargando más...</Text>
                                        </View>
                                    ) : (
                                        <TouchableOpacity
                                            onPress={() => {
                                                info('PostsList', 'Manual load more button (CSS Columns) pressed')
                                                triggerLoadMore()
                                            }}
                                            disabled={isFetchingMoreRef.current || masonryPagingLockRef.current}
                                            style={{
                                                backgroundColor: theme.colors.colorPrimary,
                                                paddingHorizontal: 32,
                                                paddingVertical: 14,
                                                borderRadius: 24,
                                                shadowColor: '#000',
                                                shadowOffset: { width: 0, height: 2 },
                                                shadowOpacity: 0.2,
                                                shadowRadius: 8,
                                                elevation: 5,
                                                opacity: isFetchingMoreRef.current || masonryPagingLockRef.current ? 0.5 : 1,
                                            }}
                                        >
                                            <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600', textAlign: 'center' }}>
                                                Cargar más contenidos
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                            </>
                        ) : isMasonryUI ? (
                            <>
                                {debug('PostsList', 'Rendering Masonry ScrollView', {
                                    masonryColumnsCount: masonryColumns.length,
                                    totalPosts: (dataPosts as any[])?.length,
                                    postsInColumns: masonryColumns.map((col) => col.length),
                                })}
                                <ScrollView
                                    ref={masonryScrollRef}
                                    onScroll={onMasonryScroll}
                                    scrollEventThrottle={16}
                                    style={[{ flex: 1 }, Platform.OS === 'web' && { backfaceVisibility: 'hidden' }]}
                                    contentContainerStyle={{ padding: 8, flexDirection: 'row', alignItems: 'flex-start', paddingBottom: 80 }}
                                >
                                    {masonryColumns.map((col, colIdx) => (
                                        <View
                                            key={`masonry-col-${colIdx}`}
                                            style={{
                                                flex: 1 / Math.max(1, effectiveCols),
                                                paddingLeft: colIdx === 0 ? 0 : horizontalGutter,
                                                backfaceVisibility: Platform.OS === 'web' ? 'hidden' : undefined,
                                            }}
                                        >
                                            {col.map(({ post, originalIndex }) => (
                                                <MasonryColumnCard
                                                    key={(post as any)._id}
                                                    post={post}
                                                    originalIndex={originalIndex}
                                                    colIdx={colIdx}
                                                />
                                            ))}
                                        </View>
                                    ))}

                                    {/* Botón de cargar más - siempre visible al final */}
                                    <View
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            bottom: 16,
                                            alignItems: 'center',
                                            pointerEvents: 'box-none',
                                        }}
                                    >
                                        {isAppending ? (
                                            <View
                                                style={{
                                                    backgroundColor: '#fff',
                                                    paddingHorizontal: 24,
                                                    paddingVertical: 12,
                                                    borderRadius: 24,
                                                    shadowColor: '#000',
                                                    shadowOffset: { width: 0, height: 2 },
                                                    shadowOpacity: 0.1,
                                                    shadowRadius: 8,
                                                    elevation: 4,
                                                }}
                                            >
                                                {React.createElement(require('../customUtils/Components/Shimmer').default, {
                                                    style: { width: 120, height: 18, borderRadius: 12 },
                                                })}
                                                <Text style={{ marginTop: 6, fontSize: 12, color: '#666', textAlign: 'center' }}>
                                                    Cargando más...
                                                </Text>
                                            </View>
                                        ) : (
                                            <TouchableOpacity
                                                onPress={() => {
                                                    info('PostsList', 'Manual load more button pressed')
                                                    triggerLoadMore()
                                                }}
                                                disabled={isFetchingMoreRef.current || masonryPagingLockRef.current}
                                                style={{
                                                    backgroundColor: theme.colors.colorPrimary,
                                                    paddingHorizontal: 32,
                                                    paddingVertical: 14,
                                                    borderRadius: 24,
                                                    shadowColor: '#000',
                                                    shadowOffset: { width: 0, height: 2 },
                                                    shadowOpacity: 0.2,
                                                    shadowRadius: 8,
                                                    elevation: 5,
                                                    opacity: isFetchingMoreRef.current || masonryPagingLockRef.current ? 0.5 : 1,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        color: '#fff',
                                                        fontSize: 15,
                                                        fontWeight: '600',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Cargar más posts
                                                </Text>
                                            </TouchableOpacity>
                                        )}
                                    </View>
                                </ScrollView>
                            </>
                        ) : (
                            <FlashList
                                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} progressViewOffset={10} />}
                                ref={mainViewRef}
                                data={dataPosts as PostItem[]}
                                keyExtractor={(item: PostItem) => item._id.toString()}
                                showsVerticalScrollIndicator={false}
                                style={{ overflow: 'visible' }}
                                onScroll={(e) => {
                                    try {
                                        lastScrollYRef.current = e?.nativeEvent?.contentOffset?.y || 0

                                        // Pausar todos los videos mientras se hace scroll
                                        mediaSingleton.pauseAllExcept(null)

                                        // Limpiar timer anterior
                                        if (scrollTimerRef.current) {
                                            clearTimeout(scrollTimerRef.current)
                                        }

                                        // Después de 150ms sin scroll, permitir que el video visible se reproduzca
                                        scrollTimerRef.current = setTimeout(() => {
                                            // El video visible se reanudará solo si el usuario había dado play manualmente
                                            scrollTimerRef.current = null
                                        }, 150)
                                    } catch {}
                                }}
                                overrideItemLayout={(layout: { size: number; span?: number }, item: PostItem, index: number) => {
                                    try {
                                        const id = (item as any)?._id
                                        let h = SizeCache.getPostHeight(id)
                                        if (typeof h !== 'number' || !(h > 0)) {
                                            const est = estimatePostHeight(item, width - 16)
                                            if (est > 0) {
                                                h = est
                                                // Cachear estimación para próximas pasadas
                                                SizeCache.setPostHeight(id, est)
                                            }
                                        }
                                        if (typeof h === 'number' && h > 0) {
                                            layout.size = h
                                        }
                                    } catch {}
                                }}
                                viewabilityConfig={{
                                    waitForInteraction: false,
                                    itemVisiblePercentThreshold: 50,
                                    minimumViewTime: 300,
                                }}
                                onViewableItemsChanged={onItemViewedChange as any}
                                renderItem={renderItem as any}
                                onEndReached={isWeb() ? undefined : handleEndReached}
                                ListFooterComponent={
                                    Platform.OS === 'web' ? (
                                        <View style={{ width: '100%', alignItems: 'center', paddingVertical: 16 }}>
                                            {isAppending ? (
                                                <View
                                                    style={{
                                                        backgroundColor: '#fff',
                                                        paddingHorizontal: 24,
                                                        paddingVertical: 12,
                                                        borderRadius: 24,
                                                        shadowColor: '#000',
                                                        shadowOffset: { width: 0, height: 2 },
                                                        shadowOpacity: 0.1,
                                                        shadowRadius: 8,
                                                        elevation: 4,
                                                    }}
                                                >
                                                    {React.createElement(require('../customUtils/Components/Shimmer').default, {
                                                        style: { width: 140, height: 18, borderRadius: 12 },
                                                    })}
                                                    <Text style={{ marginTop: 6, fontSize: 12, color: '#666', textAlign: 'center' }}>
                                                        Cargando más...
                                                    </Text>
                                                </View>
                                            ) : (
                                                <TouchableOpacity
                                                    onPress={() => {
                                                        info('PostsList', 'Manual load more button (FlashList) pressed')
                                                        triggerLoadMore()
                                                    }}
                                                    disabled={isFetchingMoreRef.current || masonryPagingLockRef.current}
                                                    style={{
                                                        backgroundColor: theme.colors.colorPrimary,
                                                        paddingHorizontal: 32,
                                                        paddingVertical: 14,
                                                        borderRadius: 24,
                                                        shadowColor: '#000',
                                                        shadowOffset: { width: 0, height: 2 },
                                                        shadowOpacity: 0.2,
                                                        shadowRadius: 8,
                                                        elevation: 5,
                                                        opacity: isFetchingMoreRef.current || masonryPagingLockRef.current ? 0.5 : 1,
                                                    }}
                                                >
                                                    <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600', textAlign: 'center' }}>
                                                        Cargar más contenidos
                                                    </Text>
                                                </TouchableOpacity>
                                            )}
                                        </View>
                                    ) : (
                                        <></>
                                    )
                                }
                                extraData={extraDataMemo}
                                numColumns={dynamicColumns}
                                key={`columns-${dynamicColumns}`}
                                contentContainerStyle={Platform.OS === 'web' ? { padding: 16, overflow: 'visible' } : { overflow: 'visible' }}
                            />
                        )}
                    </>
                )}
            </View>
        </>
    )
}

export default React.memo(PostsList)
